import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HeavyText, LightItalicText, Image, Wrapper } from './Style';
import { ReactComponent as BookmarkIcon } from '../img/bookmark.svg';
import { ReactComponent as BookmarkFillIcon } from '../img/bookmark-fill.svg';
import EmojiPicker from 'emoji-picker-react';
import { ReactComponent as ThreeDotsIcon } from '../img/three-dots.svg';
import { ReactComponent as SmileyPlusIcon } from '../img/smiley-plus.svg';
import { ReactComponent as HeartIcon } from '../img/heart.svg';
import { ReactComponent as CommentIcon } from '../img/comment.svg';
import { ReactComponent as PlayIcon } from '../img/play.svg';
import { ReactComponent as PauseIcon } from '../img/pause.svg';
import { MediaArt } from './MediaArt';
import { MediaHeader } from './MediaHeader';
import { Toast } from './Toast';
import ThumbPlayButton from './ThumbPlayButton';
import GridSpot from './GridSpot';
import { useSaveListToAppleMusic } from '../hooks/useSaveListToAppleMusic';
import Post from '../types/Post';
import Comment from '../types/Comment';
import Comments from './Comments';
import ListCarousel from './ListCarousel';
import { useAuth } from '../context/auth';
import { usePlayback } from '../context/playback';
import { usePlayCounts } from '../context/playCounts';
import api, { API_BASE } from '../api';
import { trackEvent } from '../analytics';
import formatText from '../formatText';
import ShareModal from './ShareModal';
import MentionInput from './MentionInput';
import {
  tokensToBracketText,
  mentionsToEditState,
  enrichOrderedMentions,
  resolveMusicMentions,
} from '../mentionUtils';
import { isSpecialAvatarUser, SpecialAvatar } from './UserBadge';
import { IconType } from 'react-icons';
import {
  SiApplemusic,
  SiSpotify,
  SiTidal,
  SiYoutubemusic,
  SiSoundcloud,
  SiPandora,
  SiBandcamp,
} from 'react-icons/si';

// --- timeAgo helper ---
function timeAgo(dateString?: string): string {
  if (!dateString) return '';
  var now = Date.now();
  var then = new Date(dateString).getTime();
  if (isNaN(then)) return '';
  var seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'now';
  var minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + 'm';
  var hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h';
  var days = Math.floor(hours / 24);
  if (days < 7) return days + 'd';
  var weeks = Math.floor(days / 7);
  return weeks + 'w';
}

// --- Avatar color from username ---
function avatarColor(username: string): string {
  var hash = 0;
  for (var i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  var hue = ((hash % 360) + 360) % 360;
  return 'hsl(' + hue + ', 55%, 55%)';
}

// --- Styled components ---

const SharedByBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35em;
  font-size: 0.72em;
  color: #888;
  padding: 0.4em var(--post-inset) 0;
  font-weight: 600;
`;

const SharedByLink = styled(Link)`
  color: #888;
  text-decoration: none;
  font-weight: 700;
  &:hover {
    color: #555;
    text-decoration: underline;
  }
`;

const PostDiv = styled.div`
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  background: white;
  overflow: hidden;
  width: 100%;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const AdminPlayCounts = styled.div`
  display: flex;
  gap: 0.75em;
  padding: 0.4em var(--post-inset);
  font-size: 0.7rem;
  color: #999;
  border-top: 1px solid #f0f0f0;
`;

const ArtworkBlock = styled.div`
  margin: 0 var(--post-inset);
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  overflow: hidden;
`;

// --- Compact variant styled components ---

const CompactMusicRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6em;
  padding: 0 var(--post-inset) 0.5em;
`;

const CompactThumbWrapper = styled.div`
  position: relative;
  width: 54px;
  height: 54px;
  flex-shrink: 0;
`;

const CompactThumb = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

const CompactMusicText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const CompactArtistLine = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactTrackLine = styled.span`
  font-size: 0.75rem;
  font-style: italic;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactAlbumLine = styled.span`
  font-size: 0.7rem;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactNoteRow = styled.div`
  padding: 0 var(--post-inset) 0.25em;
  font-size: 0.8em;
  color: #555;
  line-height: 1.4;
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  min-width: 0;
`;

const CompactNoteText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const CompactMoreLink = styled.span`
  color: #999;
  font-weight: 600;
  flex-shrink: 0;
  cursor: pointer;
`;

const CompactBottomRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  padding: 0.25em var(--post-inset);
  gap: 0.35em;
  border-top: 1px solid #f0f0f0;
`;

const UserDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  font-weight: 900;
  font-size: 0.75em;
  padding: 0.5em var(--post-inset);
`;

const UserSpacer = styled.div`
  flex: 1;
`;

const MiniFollowButton = styled.button<{ isFollowing?: boolean }>`
  background: ${function (p: any) {
    return p.isFollowing ? 'black' : 'white';
  }};
  color: ${function (p: any) {
    return p.isFollowing ? 'white' : 'black';
  }};
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.25em 0.6em;
  font-size: 1em;
  font-weight: 700;
  cursor: pointer;
`;

const ProfilePic = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarCircle = styled.div<{ bgColor: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${function (p: any) {
    return p.bgColor;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.85em;
  font-weight: 700;
  flex-shrink: 0;
`;

const UserLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  font-weight: 900;

  &:hover {
    text-decoration: underline;
  }
`;

const TimeAgoText = styled.span`
  color: #999;
  font-weight: 400;
`;

// A ranked list item's place, shown where the author would be: the list's
// header card already says who made it. Same height as the avatar beside
// which a normal post shows its author.
const RankChip = styled.span`
  min-width: 28px;
  height: 28px;
  box-sizing: border-box;
  padding: 0 0.45em;
  border-radius: 6px;
  background: #000;
  color: #fff;
  font-weight: 900;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const PostTextDiv = styled.div`
  font-size: 0.9em;
  padding: 0.5em var(--post-inset) 0.25em;
  line-height: 1.4;
  white-space: pre-wrap;
  position: relative;
`;

const PostTextInner = styled.div<{ collapsed?: boolean }>`
  ${function (p: any) {
    return p.collapsed
      ? `
    max-height: calc(1.4em * 6);
    overflow: hidden;
    position: relative;
  `
      : '';
  }}
`;

const PostTextFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1.8em;
  background: linear-gradient(transparent, white);
  pointer-events: none;
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.15em;
  font-size: 0.85em;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: #ccc;
  text-underline-offset: 2px;

  &:hover {
    color: #000;
    text-decoration-color: #000;
  }
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4em;
  padding: 0.25em var(--post-inset);
`;

const PromptPillLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  background: white;
  color: #333;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0 0.65em;
  border-radius: 999px;
  text-decoration: none;
  margin: 0;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  height: 26px;
  box-sizing: content-box;

  &:hover {
    background: #f5f5f5;
  }
`;

const TagLink = styled(Link)`
  background: #f0f0f0;
  color: #333;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2em 0.6em;
  border-radius: 999px;
  text-decoration: none;

  &:hover {
    background: #e0e0e0;
  }
`;

const BottomRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0.3em var(--post-inset);
  gap: 0.4em;
`;

const BottomLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4em;
  min-width: 0;
`;

const BottomRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  gap: 0.4em;
`;

// --- Streaming service links footer ---
// Subtle "open in a music service" affordance that sits in the post action row.
const ListenButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.15em 0.25em;
  margin: 0;
  /* Monochrome to match the adjacent "..." icon; service logos inherit this (currentColor). */
  color: #333;
  cursor: pointer;

  &:hover {
    color: #000;
  }
`;

function ExternalLinkIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// Brand logos shown when the viewer has exactly one streaming service selected.
// Services without a logo here (Deezer, Qobuz, Amazon Music) fall back to the generic icon.
const SERVICE_LOGOS: Record<string, IconType | undefined> = {
  apple_music: SiApplemusic,
  spotify: SiSpotify,
  tidal: SiTidal,
  youtube_music: SiYoutubemusic,
  soundcloud: SiSoundcloud,
  pandora: SiPandora,
  bandcamp: SiBandcamp,
};

// Which single selected service the action-row icon represents, most-preferred first.
// Apple/Tidal/Deezer lead because they support a direct open; Spotify/Bandcamp are search-based.
const SERVICE_ICON_PRIORITY = [
  'apple_music',
  'tidal',
  'deezer',
  'spotify',
  'bandcamp',
  'youtube_music',
  'soundcloud',
  'pandora',
  'qobuz',
  'amazon_music',
];

// --- Emoji name lookup ---
function emojiName(emoji: string): string {
  var names: Record<string, string> = {
    '\u2764\uFE0F': 'heart',
    '\uD83D\uDD25': 'fire',
    '\uD83D\uDCAF': '100',
    '\uD83D\uDE2D': 'sobbing',
    '\uD83D\uDE0D': 'heart eyes',
    '\uD83D\uDE4C': 'raised hands',
    '\uD83D\uDC4D': 'thumbs up',
    '\uD83D\uDE02': 'tears of joy',
    '\uD83E\uDD23': 'rolling on floor laughing',
    '\uD83E\uDD70': 'smiling with hearts',
    '\uD83D\uDE31': 'screaming',
    '\uD83E\uDD29': 'star struck',
    '\uD83D\uDE22': 'crying',
    '\uD83D\uDC94': 'broken heart',
    '\uD83C\uDFB6': 'musical notes',
    '\uD83C\uDFB5': 'musical note',
    '\uD83D\uDE4F': 'folded hands',
    '\uD83D\uDC4F': 'clapping',
    '\u2728': 'sparkles',
    '\uD83E\uDD1F': 'love you gesture',
  };
  return names[emoji] || emoji;
}

// --- Emoji-to-color mapping ---
function emojiColor(emoji: string): { border: string; bg: string; activeBg: string } {
  // Well-known emoji get hand-picked pastel colors
  var map: Record<string, { border: string; bg: string; activeBg: string }> = {
    '\u2764\uFE0F': { border: '#f9a8d4', bg: '#fdf2f8', activeBg: '#fce7f3' }, // heart → pink
    '\uD83D\uDD25': { border: '#fdba74', bg: '#fff7ed', activeBg: '#ffedd5' }, // fire → orange
    '\uD83D\uDCAF': { border: '#c4b5fd', bg: '#f5f3ff', activeBg: '#ede9fe' }, // 100 → purple
    '\uD83D\uDE2D': { border: '#93c5fd', bg: '#eff6ff', activeBg: '#dbeafe' }, // sob → blue
    '\uD83D\uDE0D': { border: '#fca5a5', bg: '#fef2f2', activeBg: '#fee2e2' }, // heart eyes → red
    '\uD83D\uDE4C': { border: '#fcd34d', bg: '#fffbeb', activeBg: '#fef3c7' }, // raised hands → yellow
    '\uD83D\uDC4D': { border: '#86efac', bg: '#f0fdf4', activeBg: '#dcfce7' }, // thumbs up → green
  };
  if (map[emoji]) return map[emoji];
  // Fallback: hash emoji char code to a hue
  var code = 0;
  for (var i = 0; i < emoji.length; i++) {
    code += emoji.charCodeAt(i);
  }
  var hue = code % 360;
  return {
    border: 'hsl(' + hue + ', 70%, 75%)',
    bg: 'hsl(' + hue + ', 60%, 96%)',
    activeBg: 'hsl(' + hue + ', 65%, 91%)',
  };
}

const SplitPill = styled.div<{ borderColor: string; bgColor: string }>`
  display: flex;
  align-items: stretch;
  border: 1.5px solid ${function (p: any) {
      return p.borderColor;
    }};
  border-radius: 999px;
  overflow: hidden;
  background: white;
  font-size: 1rem;
  height: 26px;
  box-sizing: content-box;
`;

const PillLeft = styled.button<{ bgColor: string; hoverBg: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${function (p: any) {
    return p.bgColor;
  }};
  border: none;
  padding: 0 0.5em;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  color: #333;

  &:hover {
    background: ${function (p: any) {
      return p.hoverBg;
    }};
  }
`;

const PillDivider = styled.div<{ color: string }>`
  width: 1px;
  background: ${function (p: any) {
    return p.color;
  }};
  align-self: stretch;
`;

const PillRight = styled.button<{ bgColor: string; hoverBg: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${function (p: any) {
    return p.bgColor;
  }};
  border: none;
  padding: 0 0.5em;
  cursor: pointer;
  font-size: 0.85rem;
  color: #555;
  font-weight: 400;
  min-width: 24px;

  &:hover {
    background: ${function (p: any) {
      return p.hoverBg;
    }};
  }
`;

const AddReactionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1.5px solid #ccc;
  border-radius: 999px;
  box-sizing: content-box;
  width: 26px;
  height: 26px;
  font-size: 1.2rem;
  color: #666;
  cursor: pointer;
  line-height: 1;
  padding: 0;

  &:hover {
    background: #e5e5e5;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25em;
  font-size: 0.8rem;
  color: #333;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddToQueueButton = styled.button`
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.3rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CommentsSection = styled.div`
  padding: 0 var(--post-inset);
`;

const InlineCommentsSection = styled.div`
  padding: 0 var(--post-inset) 0.5em;
  border-top: 1px solid #f0f0f0;
`;

const InlineCommentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
  padding: 0.4em 0;
`;

const InlineCommentAvatar = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 1px;
`;

const InlineCommentAvatarCircle = styled.div<{ $bgColor: string }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${function (p: any) {
    return p.$bgColor;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
`;

const InlineCommentBody = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const InlineCommentUsername = styled(Link)`
  font-weight: 900;
  text-decoration: none;
  color: inherit;
  margin-right: 0.35em;

  &:hover {
    text-decoration: underline;
  }
`;

const InlineCommentText = styled.span`
  font-weight: 300;
`;

const InlineCommentClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InlineReplyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 700;
  color: #888;
  margin-top: 0.1em;

  &:hover {
    color: #333;
  }
`;

const ViewAllCommentsLink = styled.button`
  background: none;
  border: none;
  padding: 0.3em 0;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  color: #888;
  display: block;

  &:hover {
    color: #333;
  }
`;

const InlineReplyPlaceholder = styled.button<{ $noBorder?: boolean }>`
  background: none;
  border: none;
  border-top: ${function (p: any) {
    return p.$noBorder ? 'none' : '1px solid #eee';
  }};
  padding: 0.5em 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 400;
  color: #999;

  &:hover {
    color: #666;
  }
`;

const CommentPill = styled.button<{ hasCount?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25em;
  background: white;
  border: 1.5px solid #ccc;
  border-radius: 999px;
  box-sizing: content-box;
  ${function (p: any) {
    return p.hasCount ? 'padding: 0 0.5em;' : 'padding: 0; width: 26px;';
  }}
  height: 26px;
  flex-shrink: 0;
  color: #555;
  font-size: 0.85rem;
  font-weight: 400;
  cursor: pointer;
  &:hover {
    background: #e5e5e5;
  }
  svg {
    width: 14px;
    height: 14px;
    stroke: #333;
    fill: none;
  }
`;

const CommentSheetOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1200;
  touch-action: none;
`;

const CommentSheetContainer = styled.div<{ $open: boolean }>`
  position: fixed;
  bottom: 0;
  left: 50%;
  width: 100%;
  max-width: 400px;
  max-height: 70vh;
  background: white;
  border-radius: 16px 16px 0 0;
  z-index: 1201;
  display: flex;
  flex-direction: column;
  padding-bottom: env(safe-area-inset-bottom, 0);
  transform: ${function (p: any) {
    return p.$open ? 'translate(-50%, 0)' : 'translate(-50%, 100%)';
  }};
  transition:
    transform 0.3s ease,
    max-height 0.2s ease;
`;

const CommentSheetHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  cursor: grab;
  touch-action: none;
  user-select: none;
`;

const CommentSheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #ccc;
  margin-bottom: 8px;
`;

const CommentSheetTitle = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
`;

const CommentSheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 0.5em;
`;

const EmojiPickerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.3);
`;

const EmojiPickerWrapper = styled.div`
  position: fixed;
  z-index: 1201;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: calc(100vw - 2rem);
`;

const QuickReactions = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 6px;
  background: white;
  border-radius: 12px 12px 0 0;
  border: 1px solid #ddd;
  border-bottom: none;
  width: 280px;
  max-width: 100%;
  box-sizing: border-box;
`;

const QuickReactionButton = styled.button`
  background: #f0f0f0;
  border: none;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  font-size: 1.3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e0e0e0;
  }
`;

const QueueMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
`;

const QueueMenuPopup = styled.div`
  position: fixed;
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 4px 0;
  z-index: 1101;
  min-width: 180px;
`;

const QueueMenuItem = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 0.8rem;
  color: black;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f0f0f0;
  }
`;

const SEARCHABLE_SERVICES: Array<{
  key: string;
  label: string;
  buildURL: (query: string, media: any, directURL?: string) => string;
}> = [
  {
    key: 'apple_music',
    label: 'Apple Music',
    buildURL: function (query, media) {
      if (media.apple_music_url) return media.apple_music_url;
      return 'https://music.apple.com/us/search?term=' + query;
    },
  },
  {
    key: 'spotify',
    label: 'Spotify',
    buildURL: function (query, _media, directURL) {
      if (directURL) return directURL;
      return 'https://open.spotify.com/search/' + query;
    },
  },
  {
    key: 'tidal',
    label: 'Tidal',
    buildURL: function (query, _media, directURL) {
      if (directURL) return directURL;
      return 'https://listen.tidal.com/search?q=' + query;
    },
  },
  {
    key: 'youtube_music',
    label: 'YouTube Music',
    buildURL: function (query) {
      return 'https://music.youtube.com/search?q=' + query;
    },
  },
  {
    key: 'qobuz',
    label: 'Qobuz',
    buildURL: function (query) {
      return 'https://www.qobuz.com/us-en/search?q=' + query;
    },
  },
  {
    key: 'amazon_music',
    label: 'Amazon Music',
    buildURL: function (query) {
      return 'https://music.amazon.com/search/' + query;
    },
  },
  {
    key: 'deezer',
    label: 'Deezer',
    buildURL: function (query, _media, directURL) {
      if (directURL) return directURL;
      return 'https://www.deezer.com/search/' + query;
    },
  },
  {
    key: 'soundcloud',
    label: 'SoundCloud',
    buildURL: function (query) {
      return 'https://soundcloud.com/search?q=' + query;
    },
  },
  {
    key: 'pandora',
    label: 'Pandora',
    buildURL: function (query) {
      return 'https://www.pandora.com/search/' + query;
    },
  },
  {
    key: 'bandcamp',
    label: 'Bandcamp',
    buildURL: function (query) {
      return 'https://bandcamp.com/search?q=' + query;
    },
  },
];

// Module-level cache for service URLs to avoid re-fetching
var serviceURLCache: Record<string, Record<string, string>> = {};

const ExplainerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

const ExplainerCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 360px;
  text-align: left;
  position: relative;
`;

const ExplainerTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.1rem;
  text-align: center;
`;

const ExplainerBody = styled.p`
  margin: 0 0 20px;
  font-size: 0.85rem;
  color: #444;
  line-height: 1.4;
`;

const ExplainerConnectButton = styled.button`
  background: white;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
`;

const ExplainerSecondaryButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  color: #666;
  cursor: pointer;
  width: 100%;
  margin-top: 8px;
`;

const UserListOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

const UserListCard = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 360px;
  max-height: 70vh;
  overflow-y: auto;
`;

const UserListTitle = styled.h3`
  margin: 0;
  padding: 16px;
  font-size: 1rem;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  border-radius: 16px 16px 0 0;
`;

const UserListItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: #f5f5f5;
  }
`;

const UserListPic = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserListAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
`;

const UserListName = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
`;

const UserListEmpty = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #999;
  font-size: 0.85rem;
`;

interface IPostProps {
  post: Post;
  variant?: 'expanded' | 'compact';
  // Supplied by ListDetailView so it can render its own list presentation
  // while still reusing this card's header, actions and comments.
  mediaOverride?: React.ReactNode;
  // Supplied by ListDetailView for the full list page: its item rows. When
  // present the card splits in three: header card, these rows, then a
  // separate comments card, so the items come before the conversation.
  detailItems?: React.ReactNode;
  // Supplied by ListDetailView for each item card: the card renders as a
  // normal post, but its author row shows the item's place instead of the
  // list's author: "#3" in a ranked list, "3." in a playlist, nothing when
  // number is null (a 3x3 grid).
  // A 3x3's item has no number: `gridSpot` (0 to 8) is its square instead.
  listItem?: { number: number | null; ranked: boolean; gridSpot?: number };
  // Replaces this post's own "play now" (not "add to queue"): a list item
  // plays on through the rest of its list.
  onPlayNow?: () => void;
  // Opens the comments whenever it takes a new value, on mount included:
  // ListDetailView sets it, per navigation, on the item a comment
  // notification points at, so a second notification for a card already on
  // the page opens it too.
  openCommentsKey?: string | null;
  isBookmarked?: boolean;
  userReactions?: string[];
  isAddedToLibrary?: boolean;
  isFollowing?: boolean;
  isReposted?: boolean;
  onBookmarkToggle?: (postId: string, bookmarked: boolean) => void;
  onReactionToggle?: (postId: string, emoji: string, reacted: boolean) => void;
  onFollowToggle?: (userId: string, following: boolean) => void;
  likedCommentsMap?: Record<string, boolean>;
  onCommentLikeToggle?: (commentId: string, liked: boolean) => void;
  onPostDelete?: (postId: string) => void;
  onRepostToggle?: (postId: string, reposted: boolean) => void;
  fullText?: boolean;
  followSource?: string;
}

function clampMenuPos(x: number, y: number, menuWidth: number, menuHeight: number) {
  var vw = window.innerWidth;
  var vh = window.innerHeight;
  var left = Math.max(8, Math.min(x, vw - menuWidth - 8));
  var top = Math.max(8, Math.min(y, vh - menuHeight - 8));
  return { left: left, top: top };
}

const AskCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  max-width: 400px;
  width: 100%;
  margin: 0 auto;
  padding: 0 0 0.5em;
  box-sizing: border-box;
`;

const AskQuestion = styled.p`
  margin: 0.5em var(--post-inset) 0.75em;
  font-size: 0.95rem;
  line-height: 1.4;
  color: #111;
  white-space: pre-wrap;
  word-break: break-word;
`;

// Whether the feed knows how to render this post. Mirrors the DisplayPost
// wrapper below: asks and lists are media-less and render their own cards;
// any other kind is unknown to this client; single posts need media.
export function isRenderableInFeed(post: Post): boolean {
  if (post.kind === 'ask') return true;
  // A list with no items would render an empty carousel, so treat it as
  // unrenderable rather than showing a hollow card.
  if (post.kind === 'list') return !!post.list && post.list.itemCount > 0;
  if (post.kind && post.kind !== 'single') return false;
  return !!post.media;
}

export function DisplayPost(props: IPostProps) {
  // Ask posts have no media; render the one-line card.
  if (props.post.kind === 'ask') {
    return <DisplayAsk {...props} />;
  }
  // Lists have no top-level media; the carousel stands in for the artwork.
  if (props.post.kind === 'list') {
    return <DisplayList {...props} />;
  }
  if (!props.post.media) return null;
  return <DisplayPostInner {...props} />;
}

// A compact, image-less post where someone asks the community about music.
// Reuses the shared header components, the Comments thread (which already
// supports attaching a song to a reply), and the comment-state handlers.
interface PostEditorApi {
  editing: boolean;
  editText: string;
  setEditText: (v: string) => void;
  editSaving: boolean;
  editMusicMentions: any[];
  setEditMusicMentions: (v: any[]) => void;
  editGetSubmitTextRef: React.MutableRefObject<(() => { text: string; mentions: any[] }) | null>;
  start: () => void;
  cancel: () => void;
  save: () => void;
}

// Shared edit flow for a post's text + music mentions. Used by both the full
// post card and the ask card so they share one implementation (and the planned
// `list` kind can reuse it too). Edits go through PATCH /api/posts/:id, which
// only touches text/mentions -- never media or kind.
function usePostEditor(
  post: Post,
  getDisplayText: () => string,
  onSaved: (newText: string, mentions: any[]) => void
): PostEditorApi {
  var _editing = useState(false);
  var editing = _editing[0];
  var setEditing = _editing[1];
  var _editText = useState('');
  var editText = _editText[0];
  var setEditText = _editText[1];
  var _editSaving = useState(false);
  var editSaving = _editSaving[0];
  var setEditSaving = _editSaving[1];
  var _editMusicMentions = useState<any[]>([]);
  var editMusicMentions = _editMusicMentions[0];
  var setEditMusicMentions = _editMusicMentions[1];
  var editGetSubmitTextRef = useRef<(() => { text: string; mentions: any[] }) | null>(null);

  function start() {
    var displayText = getDisplayText();
    var mentions = post.musicMentions || [];
    setEditText(mentions.length > 0 ? tokensToBracketText(displayText, mentions) : displayText);
    setEditMusicMentions(mentions.length > 0 ? mentionsToEditState(mentions) : []);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setEditMusicMentions([]);
  }

  function save() {
    var submitData = editGetSubmitTextRef.current ? editGetSubmitTextRef.current() : null;
    var finalText = (submitData ? submitData.text : null) || editText;
    var orderedMentions = submitData
      ? enrichOrderedMentions(submitData.mentions, editMusicMentions)
      : editMusicMentions;
    setEditSaving(true);

    resolveMusicMentions(orderedMentions).then(function (resolved: any[]) {
      api
        .patch('/api/posts/' + post.id, {
          text: finalText || null,
          musicMentions: resolved,
        })
        .then(function (res: any) {
          var savedText = (res && res.data && res.data.text) || finalText;
          post.musicMentions = resolved.map(function (md: any, i: number) {
            return {
              id: '',
              mediaId: md.mediaId || null,
              title: md.title,
              artistName: md.artistName,
              type: md.type,
              placeholderIndex: i,
            };
          });
          onSaved(savedText, post.musicMentions as any);
          setEditing(false);
          setEditSaving(false);
          setEditMusicMentions([]);
        })
        .catch(function () {
          window.alert('Failed to update post. Please try again.');
          setEditSaving(false);
        });
    });
  }

  return {
    editing: editing,
    editText: editText,
    setEditText: setEditText,
    editSaving: editSaving,
    editMusicMentions: editMusicMentions,
    setEditMusicMentions: setEditMusicMentions,
    editGetSubmitTextRef: editGetSubmitTextRef,
    start: start,
    cancel: cancel,
    save: save,
  };
}

// The inline text editor (mention input + Save/Cancel), shared by every post kind.
function PostEditForm(props: { editor: PostEditorApi; minRows?: number }) {
  var editor = props.editor;
  return (
    <PostTextDiv>
      <MentionInput
        value={editor.editText}
        onChange={function (e: any) {
          editor.setEditText(e.target.value);
        }}
        musicMentions={editor.editMusicMentions}
        onMusicMentionsChange={editor.setEditMusicMentions}
        getSubmitTextRef={editor.editGetSubmitTextRef}
        minRows={props.minRows || 2}
        disabled={editor.editSaving}
        autoFocus
        style={{
          width: '100%',
          fontSize: '0.9em',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.4em' }}>
        <button
          onClick={editor.save}
          disabled={editor.editSaving}
          style={{
            background: 'black',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '0.3em 0.8em',
            fontSize: '0.8em',
            fontWeight: 600,
            cursor: editor.editSaving ? 'not-allowed' : 'pointer',
            opacity: editor.editSaving ? 0.5 : 1,
          }}
        >
          {editor.editSaving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={editor.cancel}
          disabled={editor.editSaving}
          style={{
            background: 'none',
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '0.3em 0.8em',
            fontSize: '0.8em',
            cursor: editor.editSaving ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </PostTextDiv>
  );
}

// Shared reaction state + toggle (optimistic update, server reconciliation, and
// revert on failure). Used by the full post card and the ask card.
function usePostReactions(
  post: Post,
  userReactions: string[] | undefined,
  onReactionToggle?: (postId: string, emoji: string, reacted: boolean) => void
) {
  var _reactions = useState(post.reactions || []);
  var reactions = _reactions[0];
  var setReactions = _reactions[1];

  function toggle(emoji: string) {
    var postId = post.id;
    var ur = userReactions || [];
    var wasReacted = ur.indexOf(emoji) !== -1;

    trackEvent('reaction_toggled', {
      emoji: emoji,
      action: wasReacted ? 'removed' : 'added',
      post_id: postId,
    });

    // Optimistic update on reactions array
    setReactions(function (prev) {
      var found = false;
      var updated = prev.map(function (r) {
        if (r.emoji === emoji) {
          found = true;
          return { emoji: r.emoji, count: wasReacted ? r.count - 1 : r.count + 1 };
        }
        return r;
      });
      if (!found && !wasReacted) {
        updated = updated.concat([{ emoji: emoji, count: 1 }]);
      }
      return updated.filter(function (r) {
        return r.count > 0;
      });
    });
    if (onReactionToggle) {
      onReactionToggle(postId, emoji, !wasReacted);
    }

    api
      .post('/api/post-reactions/' + postId, { emoji: emoji })
      .then(function (response) {
        var reacted = response.data.reacted;
        // Reconcile if server disagrees
        if (reacted === wasReacted) {
          setReactions(function (prev) {
            var found = false;
            var updated = prev.map(function (r) {
              if (r.emoji === emoji) {
                found = true;
                return { emoji: r.emoji, count: reacted ? r.count + 1 : r.count - 1 };
              }
              return r;
            });
            if (!found && reacted) {
              updated = updated.concat([{ emoji: emoji, count: 1 }]);
            }
            return updated.filter(function (r) {
              return r.count > 0;
            });
          });
          if (onReactionToggle) {
            onReactionToggle(postId, emoji, reacted);
          }
        }
      })
      .catch(function (err) {
        // Revert optimistic update
        setReactions(function (prev) {
          var found = false;
          var updated = prev.map(function (r) {
            if (r.emoji === emoji) {
              found = true;
              return { emoji: r.emoji, count: wasReacted ? r.count + 1 : r.count - 1 };
            }
            return r;
          });
          if (!found && wasReacted) {
            updated = updated.concat([{ emoji: emoji, count: 1 }]);
          }
          return updated.filter(function (r) {
            return r.count > 0;
          });
        });
        if (onReactionToggle) {
          onReactionToggle(postId, emoji, wasReacted);
        }
        console.error('Reaction toggle failed:', err);
      });
  }

  return { reactions: reactions, toggle: toggle };
}

// The reaction chips + add button + emoji picker, shared by every post kind.
// `onShowReactors` is optional: when omitted (e.g. the ask card), the count is
// not a separate "who reacted" button.
function ReactionBar(props: {
  reactions: { emoji: string; count: number }[];
  userReactions?: string[];
  isLoggedIn: any;
  onToggle: (emoji: string) => void;
  onShowReactors?: (emoji: string) => void;
}) {
  var _showEmojiPicker = useState(false);
  var showEmojiPicker = _showEmojiPicker[0];
  var setShowEmojiPicker = _showEmojiPicker[1];
  var userReactions = props.userReactions || [];
  var isLoggedIn = props.isLoggedIn;
  return (
    <>
      {props.reactions.map(function (r) {
        var isActive = userReactions.indexOf(r.emoji) !== -1;
        var colors = emojiColor(r.emoji);
        var borderColor = isActive ? colors.border : '#ddd';
        var bgColor = isActive ? colors.bg : '#f5f5f5';
        return (
          <SplitPill key={r.emoji} borderColor={borderColor} bgColor={bgColor}>
            <PillLeft
              bgColor={isActive ? colors.bg : 'transparent'}
              hoverBg={colors.bg}
              onClick={
                isLoggedIn
                  ? function () {
                      props.onToggle(r.emoji);
                    }
                  : undefined
              }
              style={!isLoggedIn ? { cursor: 'default' } : undefined}
            >
              <span title={emojiName(r.emoji)}>{r.emoji}</span>
            </PillLeft>
            <PillDivider color={borderColor} />
            <PillRight
              bgColor="transparent"
              hoverBg={colors.bg}
              onClick={
                isLoggedIn && props.onShowReactors
                  ? function () {
                      props.onShowReactors!(r.emoji);
                    }
                  : undefined
              }
              style={!isLoggedIn || !props.onShowReactors ? { cursor: 'default' } : undefined}
            >
              {r.count}
            </PillRight>
          </SplitPill>
        );
      })}
      {isLoggedIn && (
        <AddReactionButton
          onClick={function () {
            setShowEmojiPicker(!showEmojiPicker);
          }}
        >
          +
        </AddReactionButton>
      )}
      {showEmojiPicker && (
        <>
          <EmojiPickerOverlay
            onClick={function () {
              setShowEmojiPicker(false);
            }}
          />
          <EmojiPickerWrapper>
            <QuickReactions>
              {['❤️', '🔥', '😍', '🙌', '👍', '💯'].map(function (emoji) {
                return (
                  <QuickReactionButton
                    key={emoji}
                    onClick={function () {
                      setShowEmojiPicker(false);
                      props.onToggle(emoji);
                    }}
                  >
                    {emoji}
                  </QuickReactionButton>
                );
              })}
            </QuickReactions>

            <div className="hr-emoji-picker">
              <style>{'.hr-emoji-picker .epr-category-nav { display: none !important; }'}</style>
              <EmojiPicker
                onEmojiClick={function (emojiData: any) {
                  setShowEmojiPicker(false);
                  props.onToggle(emojiData.emoji);
                }}
                width={280}
                height={300}
                skinTonesDisabled={true}
                searchDisabled={false}
                autoFocusSearch={true}
                previewConfig={{ showPreview: false }}
              />
            </div>
          </EmojiPickerWrapper>
        </>
      )}
    </>
  );
}

// Shared "who reacted / who bookmarked" list: state + fetch, used by the full
// post card and the ask card. The ask card only ever opens the reaction variant.
function usePostUserList(postId: string) {
  var _modal = useState<{ type: 'reaction'; emoji: string } | { type: 'bookmark' } | null>(null);
  var modal = _modal[0];
  var setModal = _modal[1];
  var _data = useState<
    {
      id: string;
      username: string;
      profilePictureURL: string | null;
      displayName?: string | null;
    }[]
  >([]);
  var data = _data[0];
  var setData = _data[1];
  var _loading = useState(false);
  var loading = _loading[0];
  var setLoading = _loading[1];

  function open(m: { type: 'reaction'; emoji: string } | { type: 'bookmark' }) {
    setModal(m);
    setData([]);
    setLoading(true);
    var url =
      m.type === 'reaction'
        ? '/api/post-reactions/' + postId + '/' + encodeURIComponent(m.emoji)
        : '/api/bookmarks/users/' + postId;
    api
      .get(url)
      .then(function (response) {
        setData(response.data);
        setLoading(false);
      })
      .catch(function () {
        setLoading(false);
      });
  }

  function close() {
    setModal(null);
  }

  return { modal: modal, data: data, loading: loading, open: open, close: close };
}

function UserListModal(props: {
  modal: { type: 'reaction'; emoji: string } | { type: 'bookmark' } | null;
  data: {
    id: string;
    username: string;
    profilePictureURL: string | null;
    displayName?: string | null;
  }[];
  loading: boolean;
  onClose: () => void;
}) {
  if (!props.modal) return null;
  var modal = props.modal;
  return (
    <UserListOverlay onClick={props.onClose}>
      <UserListCard
        onClick={function (e: any) {
          e.stopPropagation();
        }}
      >
        <UserListTitle>
          {modal.type === 'reaction' ? modal.emoji + ' Reactions' : 'Bookmarked by'}
        </UserListTitle>
        {props.loading && <UserListEmpty>Loading...</UserListEmpty>}
        {!props.loading && props.data.length === 0 && <UserListEmpty>No users yet</UserListEmpty>}
        {!props.loading &&
          props.data.map(function (u) {
            return (
              <UserListItem key={u.id} to={'/profile/' + u.username} onClick={props.onClose}>
                {u.profilePictureURL ? (
                  <UserListPic src={API_BASE + u.profilePictureURL} alt={u.username} />
                ) : (
                  <UserListAvatar style={{ background: avatarColor(u.username) }}>
                    {u.username.charAt(0).toUpperCase()}
                  </UserListAvatar>
                )}
                <div>
                  <UserListName>{u.displayName || u.username}</UserListName>
                  {u.displayName && (
                    <div style={{ color: '#777', fontSize: '0.75rem' }}>@{u.username}</div>
                  )}
                </div>
              </UserListItem>
            );
          })}
      </UserListCard>
    </UserListOverlay>
  );
}

function DisplayAsk(props: IPostProps) {
  var post = props.post;
  var user = post.user;
  var userInitial = user.username ? user.username.charAt(0).toUpperCase() : '?';

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens && authTokens.token;
  var isOwnPost = isLoggedIn && authTokens && authTokens.user && authTokens.user.id === user.id;

  var _displayText = useState(post.text || '');
  var displayText = _displayText[0];
  var setDisplayText = _displayText[1];
  var editor = usePostEditor(
    post,
    function () {
      return displayText;
    },
    function (newText: string) {
      setDisplayText(newText);
    }
  );

  var rx = usePostReactions(post, props.userReactions, props.onReactionToggle);

  var ul = usePostUserList(post.id);

  var _postMenuPos = useState<{ x: number; y: number; btnTop?: number } | null>(null);
  var postMenuPos = _postMenuPos[0];
  var setPostMenuPos = _postMenuPos[1];

  function handleAskMenuClick(e: any) {
    var rect = e.currentTarget.getBoundingClientRect();
    setPostMenuPos({ x: rect.right, y: rect.bottom, btnTop: rect.top });
  }

  function handleEditStart() {
    setPostMenuPos(null);
    editor.start();
  }

  function handleDelete() {
    setPostMenuPos(null);
    if (!window.confirm('Delete this post?')) return;
    api
      .delete('/api/posts/' + post.id)
      .then(function () {
        if (props.onPostDelete) {
          props.onPostDelete(post.id);
        }
      })
      .catch(function () {
        window.alert('Failed to delete post. Please try again.');
      });
  }

  var _comments = useState<Comment[]>(post.comments || []);
  var comments = _comments[0];
  var setComments = _comments[1];

  function handleCommentAdded(comment: Comment) {
    if (comment.parentCommentId) {
      setComments(function (prev) {
        return prev.map(function (c) {
          if (c.id === comment.parentCommentId) {
            var existingReplies = c.replies || [];
            return Object.assign({}, c, { replies: existingReplies.concat([comment]) });
          }
          return c;
        });
      });
    } else {
      setComments(function (prev) {
        return prev.concat([comment]);
      });
    }
  }

  function handleCommentDeleted(commentId: string, parentCommentId?: string) {
    if (parentCommentId) {
      setComments(function (prev) {
        return prev.map(function (c) {
          if (c.id === parentCommentId && c.replies) {
            return Object.assign({}, c, {
              replies: c.replies.filter(function (r) {
                return r.id !== commentId;
              }),
            });
          }
          return c;
        });
      });
    } else {
      setComments(function (prev) {
        return prev.filter(function (c) {
          return c.id !== commentId;
        });
      });
    }
  }

  function handleCommentEdited(commentId: string, newText: string, newMusicMentions?: any[]) {
    setComments(function (prev) {
      return prev.map(function (c) {
        if (c.id === commentId) {
          return Object.assign({}, c, { text: newText, musicMentions: newMusicMentions || [] });
        }
        if (c.replies) {
          return Object.assign({}, c, {
            replies: c.replies.map(function (r) {
              if (r.id === commentId) {
                return Object.assign({}, r, {
                  text: newText,
                  musicMentions: newMusicMentions || [],
                });
              }
              return r;
            }),
          });
        }
        return c;
      });
    });
  }

  return (
    <AskCard>
      <UserDiv>
        {isSpecialAvatarUser(user.username) ? (
          <SpecialAvatar size={28} />
        ) : user.profilePictureURL ? (
          <ProfilePic src={API_BASE + user.profilePictureURL} alt={user.username} />
        ) : (
          <AvatarCircle bgColor={avatarColor(user.username)}>{userInitial}</AvatarCircle>
        )}
        <UserLink to={'/profile/' + user.username}>{user.username}</UserLink>
        <TimeAgoText>{timeAgo(post.created_at)}</TimeAgoText>
        <UserSpacer />
        {isOwnPost && (
          <IconButton onClick={handleAskMenuClick}>
            <ThreeDotsIcon width="18" height="18" />
          </IconButton>
        )}
      </UserDiv>

      {editor.editing ? (
        <PostEditForm editor={editor} />
      ) : (
        <AskQuestion
          dangerouslySetInnerHTML={{
            __html: formatText(displayText, post.musicMentions, post.media?.artist),
          }}
        />
      )}

      <BottomLeft style={{ margin: '0.25em var(--post-inset) 0.5em' }}>
        <ReactionBar
          reactions={rx.reactions}
          userReactions={props.userReactions}
          isLoggedIn={isLoggedIn}
          onToggle={rx.toggle}
          onShowReactors={function (emoji) {
            ul.open({ type: 'reaction', emoji: emoji });
          }}
        />
      </BottomLeft>

      <CommentsSection>
        <Comments
          post={post}
          comments={comments}
          onCommentAdded={handleCommentAdded}
          fullText={true}
          likedCommentsMap={props.likedCommentsMap}
          onCommentLikeToggle={props.onCommentLikeToggle}
          onCommentDeleted={handleCommentDeleted}
          onCommentEdited={handleCommentEdited}
        />
      </CommentsSection>

      {postMenuPos && (
        <>
          <QueueMenuOverlay
            onClick={function () {
              setPostMenuPos(null);
            }}
          />
          <QueueMenuPopup
            ref={function (el: HTMLDivElement | null) {
              if (!el || !postMenuPos) return;
              var menuW = el.offsetWidth || 200;
              var menuH = el.offsetHeight || 200;
              var vw = window.innerWidth;
              var vh = window.innerHeight;
              var left = Math.max(8, Math.min(postMenuPos.x - menuW, vw - menuW - 8));
              var top = postMenuPos.y + 4;
              if (top + menuH > vh - 8) {
                top = (postMenuPos.btnTop || postMenuPos.y) - menuH - 4;
              }
              top = Math.max(8, top);
              el.style.left = left + 'px';
              el.style.top = top + 'px';
            }}
            style={{
              maxHeight: '80vh',
              overflowY: 'auto' as const,
              position: 'fixed',
              zIndex: 1101,
            }}
          >
            <QueueMenuItem onClick={handleEditStart}>Edit Post</QueueMenuItem>
            {props.onPostDelete && (
              <QueueMenuItem onClick={handleDelete} style={{ color: 'tomato' }}>
                Delete Post
              </QueueMenuItem>
            )}
          </QueueMenuPopup>
        </>
      )}

      <UserListModal modal={ul.modal} data={ul.data} loading={ul.loading} onClose={ul.close} />
    </AskCard>
  );
}

// A list post: no top-level media, a ListCarousel where the artwork would be.
//
// Deliberately a sibling of DisplayAsk rather than a branch inside
// DisplayPostInner. DisplayPostInner dereferences `media` throughout (title,
// playback, artwork, streaming links) and a list's media is always null, so
// making it list-aware would mean guarding dozens of call sites. Both media-less
// kinds instead reuse the shared hooks and sub-components.
//
// ListCarousel renders the list's own title, item count and View List button,
// so this only supplies the surrounding card.
function DisplayList(props: IPostProps) {
  var post = props.post;
  var user = post.user;
  var userInitial = user.username ? user.username.charAt(0).toUpperCase() : '?';

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens && authTokens.token;
  var isOwnPost = isLoggedIn && authTokens && authTokens.user && authTokens.user.id === user.id;

  var _displayText = useState(post.text || '');
  var displayText = _displayText[0];
  var setDisplayText = _displayText[1];
  var editor = usePostEditor(
    post,
    function () {
      return displayText;
    },
    function (newText: string) {
      setDisplayText(newText);
    }
  );

  var rx = usePostReactions(post, props.userReactions, props.onReactionToggle);
  var ul = usePostUserList(post.id);
  var navigate = useNavigate();

  var _postMenuPos = useState<{ x: number; y: number; btnTop?: number } | null>(null);
  var postMenuPos = _postMenuPos[0];
  var setPostMenuPos = _postMenuPos[1];

  // Save the list to Apple Music: the Apple Music icon beside the dots, as
  // a post's icon opens its music there, on the feed card and the list page.
  // Not on a draft (never published, or unpublished): its author is still
  // working on it.
  var appleMusicSave = useSaveListToAppleMusic(post.id);
  var offerAppleMusicSave = appleMusicSave.canSave && post.status !== 'unpublished';

  function handleListMenuClick(e: any) {
    var rect = e.currentTarget.getBoundingClientRect();
    setPostMenuPos({ x: rect.right, y: rect.bottom, btnTop: rect.top });
  }

  function handleEditStart() {
    setPostMenuPos(null);
    editor.start();
  }

  function handleDelete() {
    setPostMenuPos(null);
    if (!window.confirm('Delete this list?')) return;
    api
      .delete('/api/posts/' + post.id)
      .then(function () {
        if (props.onPostDelete) {
          props.onPostDelete(post.id);
        }
      })
      .catch(function () {
        window.alert('Failed to delete list. Please try again.');
      });
  }

  var _comments = useState<Comment[]>(post.comments || []);
  var comments = _comments[0];
  var setComments = _comments[1];

  function handleCommentAdded(comment: Comment) {
    if (comment.parentCommentId) {
      setComments(function (prev) {
        return prev.map(function (c) {
          if (c.id === comment.parentCommentId) {
            var existingReplies = c.replies || [];
            return Object.assign({}, c, { replies: existingReplies.concat([comment]) });
          }
          return c;
        });
      });
    } else {
      setComments(function (prev) {
        return prev.concat([comment]);
      });
    }
  }

  function handleCommentDeleted(commentId: string, parentCommentId?: string) {
    if (parentCommentId) {
      setComments(function (prev) {
        return prev.map(function (c) {
          if (c.id === parentCommentId && c.replies) {
            return Object.assign({}, c, {
              replies: c.replies.filter(function (r) {
                return r.id !== commentId;
              }),
            });
          }
          return c;
        });
      });
    } else {
      setComments(function (prev) {
        return prev.filter(function (c) {
          return c.id !== commentId;
        });
      });
    }
  }

  function handleCommentEdited(commentId: string, newText: string, newMusicMentions?: any[]) {
    setComments(function (prev) {
      return prev.map(function (c) {
        if (c.id === commentId) {
          return Object.assign({}, c, { text: newText, musicMentions: newMusicMentions || [] });
        }
        if (c.replies) {
          return Object.assign({}, c, {
            replies: c.replies.map(function (r) {
              if (r.id === commentId) {
                return Object.assign({}, r, {
                  text: newText,
                  musicMentions: newMusicMentions || [],
                });
              }
              return r;
            }),
          });
        }
        return c;
      });
    });
  }

  // The full list page splits this card around its item rows (detailItems).
  // There, a logged-out viewer with nothing to read gets no reactions row and
  // no comments card: both rendered as empty padding, a blank block under the
  // list's header.
  var isDetail = props.detailItems !== undefined;
  var showReactions = !isDetail || !!isLoggedIn || rx.reactions.length > 0 || !!post.prompt;
  var showComments = !isDetail || !!isLoggedIn || comments.length > 0;

  // The prompt pill sits with the reactions, as on a single post's card.
  var reactionsRow = (
    <BottomLeft style={{ margin: '0.25em var(--post-inset) 0.5em' }}>
      {post.prompt && (
        <PromptPillLink to={'/prompts/' + post.prompt.id}>
          {post.prompt.name || post.prompt.text}
        </PromptPillLink>
      )}
      <ReactionBar
        reactions={rx.reactions}
        userReactions={props.userReactions}
        isLoggedIn={isLoggedIn}
        onToggle={rx.toggle}
        onShowReactors={function (emoji) {
          ul.open({ type: 'reaction', emoji: emoji });
        }}
      />
    </BottomLeft>
  );

  var commentsSection = (
    <CommentsSection>
      <Comments
        post={post}
        comments={comments}
        onCommentAdded={handleCommentAdded}
        fullText={true}
        likedCommentsMap={props.likedCommentsMap}
        onCommentLikeToggle={props.onCommentLikeToggle}
        onCommentDeleted={handleCommentDeleted}
        onCommentEdited={handleCommentEdited}
        standalone={isDetail}
      />
    </CommentsSection>
  );

  return (
    <>
      <AskCard>
        <UserDiv>
          {isSpecialAvatarUser(user.username) ? (
            <SpecialAvatar size={28} />
          ) : user.profilePictureURL ? (
            <ProfilePic src={API_BASE + user.profilePictureURL} alt={user.username} />
          ) : (
            <AvatarCircle bgColor={avatarColor(user.username)}>{userInitial}</AvatarCircle>
          )}
          <UserLink to={'/profile/' + user.username}>{user.username}</UserLink>
          <TimeAgoText>{timeAgo(post.created_at)}</TimeAgoText>
          <UserSpacer />
          {offerAppleMusicSave && (
            <ListenButton
              onClick={appleMusicSave.save}
              // Busy rather than disabled: a disabled button drops focus,
              // which then has nowhere to return to. Repeat taps are ignored
              // while a save is in flight.
              aria-disabled={appleMusicSave.saving}
              title="Create playlist on Apple Music"
              aria-label="Create playlist on Apple Music"
              aria-busy={appleMusicSave.saving}
              style={appleMusicSave.saving ? { opacity: 0.4 } : undefined}
            >
              <SiApplemusic size={18} />
            </ListenButton>
          )}
          {isOwnPost && (
            <IconButton onClick={handleListMenuClick} aria-label="More">
              <ThreeDotsIcon width="18" height="18" />
            </IconButton>
          )}
        </UserDiv>

        {/* mediaOverride lets ListDetailView supply its own presentation while
          reusing this card's header, actions and comments. */}
        {props.mediaOverride
          ? props.mediaOverride
          : post.list && <ListCarousel list={post.list} postId={post.id} status={post.status} />}

        {editor.editing ? (
          <PostEditForm editor={editor} />
        ) : (
          displayText && (
            <AskQuestion
              dangerouslySetInnerHTML={{
                __html: formatText(displayText, post.musicMentions, undefined),
              }}
            />
          )
        )}

        {showReactions && reactionsRow}

        {!isDetail && commentsSection}

        {postMenuPos && (
          <>
            <QueueMenuOverlay
              onClick={function () {
                setPostMenuPos(null);
              }}
            />
            <QueueMenuPopup
              ref={function (el: HTMLDivElement | null) {
                if (!el || !postMenuPos) return;
                var menuW = el.offsetWidth || 200;
                var menuH = el.offsetHeight || 200;
                var vw = window.innerWidth;
                var vh = window.innerHeight;
                var left = Math.max(8, Math.min(postMenuPos.x - menuW, vw - menuW - 8));
                var top = postMenuPos.y + 4;
                if (top + menuH > vh - 8) {
                  top = (postMenuPos.btnTop || postMenuPos.y) - menuH - 4;
                }
                top = Math.max(8, top);
                el.style.left = left + 'px';
                el.style.top = top + 'px';
              }}
              style={{
                maxHeight: '80vh',
                overflowY: 'auto' as const,
                position: 'fixed',
                zIndex: 1101,
              }}
            >
              <QueueMenuItem onClick={handleEditStart}>Edit Caption</QueueMenuItem>
              <QueueMenuItem
                onClick={function () {
                  setPostMenuPos(null);
                  navigate('/list/edit?listId=' + post.id);
                }}
              >
                Edit List
              </QueueMenuItem>
              {props.onPostDelete && (
                <QueueMenuItem onClick={handleDelete} style={{ color: 'tomato' }}>
                  Delete List
                </QueueMenuItem>
              )}
            </QueueMenuPopup>
          </>
        )}

        <UserListModal modal={ul.modal} data={ul.data} loading={ul.loading} onClose={ul.close} />
        {appleMusicSave.ui}
      </AskCard>
      {isDetail && props.detailItems}
      {isDetail && showComments && (
        <AskCard style={{ marginTop: '0.75em', paddingTop: '0.5em' }}>{commentsSection}</AskCard>
      )}
    </>
  );
}

function DisplayPostInner(props: IPostProps) {
  // media is guaranteed non-null by the DisplayPost wrapper guard
  var media = props.post.media!;
  // Apply artwork override if the post has one (e.g. song posted from a specific album)
  if (props.post.artworkURLOverride) {
    media = Object.assign({}, media, { artwork_url: props.post.artworkURLOverride });
  }
  var title = media.type === 'album' ? media.album_title : media.song_title;
  var user = props.post.user;
  // A list item is reposted, edited and deleted as part of its list (the
  // server refuses all three on an item), so its menu leaves them out.
  var isListItem = !!props.post.parentListId;
  var _comments = useState<Comment[]>(props.post.comments || []);
  var comments = _comments[0];
  var setComments = _comments[1];

  var _addState = useState<'idle' | 'adding' | 'addedLibrary' | 'error'>(
    props.isAddedToLibrary ? 'addedLibrary' : 'idle'
  );
  var addState = _addState[0];
  var setAddState = _addState[1];

  var _fetchingPreview = useState(false);
  var fetchingPreview = _fetchingPreview[0];
  var setFetchingPreview = _fetchingPreview[1];

  var _bookmarkCount = useState(props.post.bookmarkCount || 0);
  var bookmarkCount = _bookmarkCount[0];
  var setBookmarkCount = _bookmarkCount[1];

  var rx = usePostReactions(props.post, props.userReactions, props.onReactionToggle);

  var _libraryAddCount = useState(props.post.libraryAddCount || 0);
  var libraryAddCount = _libraryAddCount[0];
  var setLibraryAddCount = _libraryAddCount[1];

  var cachedPreviewRef = useRef<{ previewURL: string | null; catalogID: string | null } | null>(
    null
  );

  var _menuPos = useState<{ x: number; y: number } | null>(null);
  var menuPos = _menuPos[0];
  var setMenuPos = _menuPos[1];

  var _addMenuPos = useState<{ x: number; y: number } | null>(null);
  var addMenuPos = _addMenuPos[0];
  var setAddMenuPos = _addMenuPos[1];

  var _postMenuPos = useState<{ x: number; y: number; btnTop?: number } | null>(null);
  var postMenuPos = _postMenuPos[0];
  var setPostMenuPos = _postMenuPos[1];

  var _showShareModal = useState(false);
  var showShareModal = _showShareModal[0];
  var setShowShareModal = _showShareModal[1];

  var _showCommentSheet = useState(false);
  var showCommentSheet = _showCommentSheet[0];
  var setShowCommentSheet = _showCommentSheet[1];

  var _sheetAnimating = useState(false);
  var sheetAnimating = _sheetAnimating[0];
  var setSheetAnimating = _sheetAnimating[1];

  var _sheetReady = useState(false);
  var sheetReady = _sheetReady[0];
  var setSheetReady = _sheetReady[1];

  var _showInlineComments = useState(false);
  var showInlineComments = _showInlineComments[0];
  var setShowInlineComments = _showInlineComments[1];

  var _showExplainer = useState(false);
  var showExplainer = _showExplainer[0];
  var setShowExplainer = _showExplainer[1];

  var pendingAddPos = useRef<{ x: number; y: number } | null>(null);

  var _showPreviewExplainer = useState(false);
  var showPreviewExplainer = _showPreviewExplainer[0];
  var setShowPreviewExplainer = _showPreviewExplainer[1];

  var postTextRef = useRef<HTMLDivElement>(null);
  var _textOverflows = useState(false);
  var textOverflows = _textOverflows[0];
  var setTextOverflows = _textOverflows[1];
  var _textExpanded = useState(false);
  var textExpanded = _textExpanded[0];
  var setTextExpanded = _textExpanded[1];
  var _textMeasured = useState(false);
  var textMeasured = _textMeasured[0];
  var setTextMeasured = _textMeasured[1];

  var _displayText = useState(props.post.text || '');
  var displayText = _displayText[0];
  var setDisplayText = _displayText[1];
  var editor = usePostEditor(
    props.post,
    function () {
      return displayText;
    },
    function (newText: string) {
      setDisplayText(newText);
    }
  );

  useEffect(
    function () {
      var el = postTextRef.current;
      if (el && !props.fullText) {
        var lineHeight =
          parseFloat(getComputedStyle(el).lineHeight) ||
          parseFloat(getComputedStyle(el).fontSize) * 1.4;
        var overflow = el.scrollHeight - el.clientHeight;
        // Smart threshold: if less than ~2 lines overflow, just show it all
        setTextOverflows(overflow > lineHeight * 2);
        setTextMeasured(true);
      }
    },
    [props.post.text, props.fullText]
  );

  var sheetRef = useRef<HTMLDivElement>(null);

  var savedScrollY = useRef(0);
  var sheetWasOpen = useRef(false);

  useEffect(
    function () {
      if (showCommentSheet) {
        // Lock scroll position without jumping on iOS Safari
        savedScrollY.current = window.scrollY;
        sheetWasOpen.current = true;
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + savedScrollY.current + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
      } else if (sheetWasOpen.current) {
        // Only restore scroll if the sheet was actually open
        sheetWasOpen.current = false;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, savedScrollY.current);
      }
      return function () {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
      };
    },
    [showCommentSheet]
  );

  // Resize sheet when mobile keyboard opens/closes
  useEffect(
    function () {
      if (!showCommentSheet) return;
      var vv = window.visualViewport;
      if (!vv) return;
      function handleResize() {
        var el = sheetRef.current;
        if (!el || !vv) return;
        var vpHeight = vv.height;
        el.style.maxHeight = Math.min(vpHeight * 0.7, vpHeight - 20) + 'px';
      }
      vv.addEventListener('resize', handleResize);
      return function () {
        vv!.removeEventListener('resize', handleResize);
      };
    },
    [showCommentSheet]
  );

  var keyboardProxyRef = useRef<HTMLInputElement>(null);
  var wantsFocusRef = useRef(false);
  var _replyTarget = useState<{ commentId: string; username: string } | null>(null);
  var replyTarget = _replyTarget[0];
  var setReplyTarget = _replyTarget[1];

  // "Post about this song" from a list item: the composer opens with the
  // same media, as the song page's button does.
  function handlePostAboutItem() {
    setPostMenuPos(null);
    trackEvent('music_post_initiated', {
      type: media.type,
      title: title,
      artist: media.artist,
      source: 'list_item',
    });
    navigate('/post', { state: { media: props.post.media } });
  }

  useEffect(
    function () {
      if (props.openCommentsKey) openCommentSheet();
    },
    // Once per key: a new notification opens the comments again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.openCommentsKey]
  );

  function openCommentSheet(focusInput?: boolean) {
    // Desktop: show comments inline instead of a sheet
    var isDesktop = window.innerWidth > 600;
    if (isDesktop) {
      if (focusInput) {
        wantsFocusRef.current = true;
        setTimeout(function () {
          wantsFocusRef.current = false;
        }, 500);
      }
      setShowInlineComments(true);
      return;
    }

    if (focusInput) {
      wantsFocusRef.current = true;
    }
    // On iOS, focus a proxy input synchronously to claim the keyboard within the tap gesture
    if ((comments.length === 0 || focusInput) && keyboardProxyRef.current) {
      keyboardProxyRef.current.focus();
    }
    setShowCommentSheet(true);
    setSheetReady(false);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        setSheetAnimating(true);
      });
    });
    // Delay auto-focus until after slide animation so keyboard doesn't disrupt positioning
    setTimeout(function () {
      setSheetReady(true);
    }, 350);
    setTimeout(function () {
      wantsFocusRef.current = false;
    }, 500);
  }

  function closeCommentSheet() {
    wantsFocusRef.current = false;
    setReplyTarget(null);
    setSheetAnimating(false);
    setSheetReady(false);
    setTimeout(function () {
      setShowCommentSheet(false);
    }, 300);
  }

  var dragStartY = useRef<number | null>(null);
  var dragOffset = useRef(0);

  function handleDragStart(e: any) {
    var y = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    dragStartY.current = y;
    dragOffset.current = 0;
    var el = sheetRef.current;
    if (el) el.style.transition = 'none';
  }

  function handleDragMove(e: any) {
    if (dragStartY.current === null) return;
    var y = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    var delta = y - dragStartY.current;
    if (delta < 0) delta = 0; // only allow dragging down
    dragOffset.current = delta;
    var el = sheetRef.current;
    if (el) el.style.transform = 'translate(-50%, ' + delta + 'px)';
  }

  function handleDragEnd() {
    if (dragStartY.current === null) return;
    dragStartY.current = null;
    var el = sheetRef.current;
    if (el) el.style.transition = 'transform 0.3s ease, max-height 0.2s ease';
    if (dragOffset.current > 100) {
      closeCommentSheet();
    } else {
      // Snap back
      if (el) el.style.transform = 'translate(-50%, 0)';
    }
    dragOffset.current = 0;
  }

  var ul = usePostUserList(props.post.id);

  var pendingPlayRect = useRef<{ left: number; top: number } | null>(null);

  var playback = usePlayback();
  var navigate = useNavigate();
  var location = useLocation();
  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens && authTokens.token;
  // Already declared at top of function via non-null assertion
  var canAdd = media.type === 'song' ? !!media.isrc : !!media.upc;
  var canPlay = !!media.isrc || !!media.upc;
  var isOwnPost = isLoggedIn && authTokens && authTokens.user && authTokens.user.id === user.id;
  var playCountsCtx = usePlayCounts();

  useEffect(
    function () {
      if (playCountsCtx.isAdmin && props.post.id) {
        playCountsCtx.register(props.post.id);
      }
    },
    [playCountsCtx.isAdmin, props.post.id]
  );

  var playCounts = playCountsCtx.isAdmin ? playCountsCtx.counts[props.post.id] : null;
  var isSomethingPlaying =
    playback.playbackState === 'playing' ||
    playback.playbackState === 'paused' ||
    playback.playbackState === 'loading';

  function handleFollowToggle() {
    var wasFollowing = !!props.isFollowing;
    if (props.onFollowToggle) {
      props.onFollowToggle(user.id, !wasFollowing);
    }
    if (!wasFollowing) {
      trackEvent('follow_completed', {
        source: props.followSource || 'unknown',
        target_user: user.username,
      });
    }
    api.post('/api/follows/' + user.id).catch(function () {
      if (props.onFollowToggle) {
        props.onFollowToggle(user.id, wasFollowing);
      }
    });
  }

  function handleCommentAdded(comment: Comment) {
    if (comment.parentCommentId) {
      // Nest reply under its parent comment
      setComments(function (prev) {
        return prev.map(function (c) {
          if (c.id === comment.parentCommentId) {
            var existingReplies = c.replies || [];
            return Object.assign({}, c, { replies: existingReplies.concat([comment]) });
          }
          return c;
        });
      });
    } else {
      setComments(function (prev) {
        return prev.concat([comment]);
      });
    }
  }

  var _serviceURLs = useState<Record<string, string> | null>(null);
  var serviceURLs = _serviceURLs[0];
  var setServiceURLs = _serviceURLs[1];

  // Position of the "Listen on..." popover opened from the action-row icon.
  var _listenPos = useState<{ x: number; y: number; btnTop?: number } | null>(null);
  var listenPos = _listenPos[0];
  var setListenPos = _listenPos[1];

  // When the action-row icon represents a lookup-based service (Tidal/Deezer), prefetch its direct
  // URL so a single tap opens the track directly instead of falling back to a search page.
  useEffect(
    function () {
      var top = getTopService();
      if (top && (top.key === 'deezer' || top.key === 'tidal')) {
        ensureServiceURLs();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [authTokens?.user?.streamingServices, props.post.media?.id]
  );

  // Lazy-fetch direct service URLs (Deezer/Tidal) for this media when a menu/popover opens.
  function ensureServiceURLs() {
    var mediaId: string = props.post.media?.id || '';
    if (!mediaId) return;
    if (serviceURLCache[mediaId]) {
      setServiceURLs(serviceURLCache[mediaId]);
      return;
    }
    var userServices = (authTokens?.user?.streamingServices || '').split(',').filter(Boolean);
    var lookupServices = userServices.filter(function (s: string) {
      return ['deezer', 'tidal'].indexOf(s) !== -1;
    });
    if (lookupServices.length > 0) {
      api
        .get('/api/service-urls', {
          params: { mediaId: mediaId, services: lookupServices.join(',') },
        })
        .then(function (res: any) {
          var urls = res.data?.urls || {};
          serviceURLCache[mediaId] = urls;
          setServiceURLs(urls);
        })
        .catch(function () {});
    }
  }

  function handlePostMenuClick(e: any) {
    var rect = e.currentTarget.getBoundingClientRect();
    setPostMenuPos({ x: rect.right, y: rect.bottom, btnTop: rect.top });
    ensureServiceURLs();
  }

  // Opens the streaming-services popover (used when no service is selected).
  function handleListenClick(e: any) {
    var rect = e.currentTarget.getBoundingClientRect();
    setListenPos({ x: rect.right, y: rect.bottom, btnTop: rect.top });
    ensureServiceURLs();
  }

  // Shared by the "..." menu and the visible streaming-links footer:
  // tracks the open, records a play event, and opens the service in a new tab.
  function openInService(
    svc: { key: string; label: string; buildURL: (q: string, m: any, d?: string) => string },
    directURL?: string
  ) {
    var songTitle = media.type === 'album' ? media.album_title : media.song_title;
    var query = encodeURIComponent((media.artist || '') + ' ' + (songTitle || ''));
    trackEvent('opened_in_service', {
      service: svc.key,
      post_id: props.post.id,
      media_type: media.type,
      artist: media.artist,
      title: songTitle,
      direct: !!directURL,
    });
    if (isLoggedIn) {
      api
        .post('/api/play-events', {
          postId: props.post.id,
          playType: 'service_open',
          service: svc.key,
        })
        .catch(function () {});
    }
    var url = svc.buildURL(query, media, directURL);
    var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
    if (svc.key === 'apple_music' && !isMobile) {
      // Apple Music on a desktop hands the link to the Music app, which would leave a new tab
      // blank, so it navigates this tab instead; Back returns to HeavyRotation.
      window.location.href = url;
    } else {
      // Everything else, and every service on a phone, opens a new tab, keeping the feed open
      // behind the service's web player or app.
      window.open(url, '_blank', 'noopener');
    }
  }

  // Builds the streaming-service menu items: the viewer's preferred services, or Apple Music +
  // Spotify (plus a "choose your services" link) when none are set. Used by the Listen popover.
  function renderServiceItems(onClose: () => void) {
    var userServices = (authTokens?.user?.streamingServices || '').split(',').filter(Boolean);
    var preferred = SEARCHABLE_SERVICES.filter(function (s) {
      return userServices.indexOf(s.key) !== -1;
    });
    var noServicesChosen = preferred.length === 0;
    if (noServicesChosen) {
      preferred = SEARCHABLE_SERVICES.filter(function (s) {
        return s.key === 'apple_music' || s.key === 'spotify';
      });
    }
    var items = preferred.map(function (svc) {
      var directURL = serviceURLs && serviceURLs[svc.key] ? serviceURLs[svc.key] : undefined;
      // Apple Music is the canonical source for every post, so it always opens directly.
      var hasDirectLink = !!directURL || svc.key === 'apple_music';
      var label = hasDirectLink ? 'Open in ' + svc.label : 'Find on ' + svc.label;
      return (
        <QueueMenuItem
          key={svc.key}
          onClick={function () {
            onClose();
            openInService(svc, directURL);
          }}
        >
          {label}
        </QueueMenuItem>
      );
    });
    if (noServicesChosen) {
      items.push(
        <QueueMenuItem
          key="choose-services"
          onClick={function () {
            onClose();
            navigate('/streaming');
          }}
          style={{ color: '#999', fontSize: '0.75rem' }}
        >
          Choose your streaming services...
        </QueueMenuItem>
      );
    }
    return items;
  }

  // The single service the action-row icon represents: the viewer's top selection by priority.
  function getTopService() {
    var userServices = (authTokens?.user?.streamingServices || '').split(',').filter(Boolean);
    var selected = SEARCHABLE_SERVICES.filter(function (s) {
      return userServices.indexOf(s.key) !== -1;
    });
    if (selected.length === 0) return null;
    selected.sort(function (a, b) {
      return SERVICE_ICON_PRIORITY.indexOf(a.key) - SERVICE_ICON_PRIORITY.indexOf(b.key);
    });
    return selected[0];
  }

  // Action-row affordance: one icon for the viewer's top selected service. Shows that service's brand
  // logo (or the generic icon if it has none) and opens it directly. With no service selected, the
  // generic icon opens the Listen popover.
  function renderListenButton() {
    const top = getTopService();
    if (!top) {
      return (
        <ListenButton
          onClick={handleListenClick}
          title="Open in a music service"
          aria-label="Open in a music service"
        >
          <ExternalLinkIcon />
        </ListenButton>
      );
    }
    var Logo = SERVICE_LOGOS[top.key];
    return (
      <ListenButton
        onClick={function () {
          openInService(top, serviceURLs ? serviceURLs[top.key] : undefined);
        }}
        title={'Open in ' + top.label}
        aria-label={'Open in ' + top.label}
      >
        {Logo ? <Logo size={18} /> : <ExternalLinkIcon />}
      </ListenButton>
    );
  }

  function handlePostDelete() {
    setPostMenuPos(null);
    if (!window.confirm('Delete this post?')) return;
    api
      .delete('/api/posts/' + props.post.id)
      .then(function () {
        if (props.onPostDelete) {
          props.onPostDelete(props.post.id);
        }
      })
      .catch(function () {
        window.alert('Failed to delete post. Please try again.');
      });
  }

  var _repostCount = useState(props.post.repostCount || 0);
  var repostCount = _repostCount[0];
  var setRepostCount = _repostCount[1];

  var _repostToast = useState<string | null>(null);
  var repostToast = _repostToast[0];
  var setRepostToast = _repostToast[1];

  function handleRepostToggle() {
    setPostMenuPos(null);
    var wasReposted = !!props.isReposted;
    var newReposted = !wasReposted;
    setRepostCount(function (prev: number) {
      return prev + (newReposted ? 1 : -1);
    });
    if (props.onRepostToggle) {
      props.onRepostToggle(props.post.id, newReposted);
    }
    setRepostToast(newReposted ? 'Reposted!' : 'Repost removed');
    setTimeout(function () {
      setRepostToast(null);
    }, 1800);
    api.post('/api/reposts/' + props.post.id).catch(function (err: any) {
      console.error('Repost toggle failed:', err);
      setRepostCount(function (prev: number) {
        return prev + (newReposted ? -1 : 1);
      });
      if (props.onRepostToggle) {
        props.onRepostToggle(props.post.id, wasReposted);
      }
      setRepostToast(null);
    });
  }

  function handleEditStart() {
    setPostMenuPos(null);
    editor.start();
  }

  function handleCommentDeleted(commentId: string, parentCommentId?: string) {
    if (parentCommentId) {
      setComments(function (prev) {
        return prev.map(function (c) {
          if (c.id === parentCommentId && c.replies) {
            return Object.assign({}, c, {
              replies: c.replies.filter(function (r) {
                return r.id !== commentId;
              }),
            });
          }
          return c;
        });
      });
    } else {
      setComments(function (prev) {
        return prev.filter(function (c) {
          return c.id !== commentId;
        });
      });
    }
  }

  function handleCommentEdited(commentId: string, newText: string, newMusicMentions?: any[]) {
    setComments(function (prev) {
      return prev.map(function (c) {
        if (c.id === commentId) {
          return Object.assign({}, c, { text: newText, musicMentions: newMusicMentions || [] });
        }
        if (c.replies) {
          return Object.assign({}, c, {
            replies: c.replies.map(function (r) {
              if (r.id === commentId) {
                return Object.assign({}, r, {
                  text: newText,
                  musicMentions: newMusicMentions || [],
                });
              }
              return r;
            }),
          });
        }
        return c;
      });
    });
  }

  function handleBookmarkToggle() {
    var postId = props.post.id;
    var wasBookmarked = !!props.isBookmarked;

    trackEvent('bookmark_toggled', {
      action: wasBookmarked ? 'removed' : 'added',
      post_id: postId,
    });

    // Optimistic update
    setBookmarkCount(function (prev) {
      return wasBookmarked ? prev - 1 : prev + 1;
    });
    if (props.onBookmarkToggle) {
      props.onBookmarkToggle(postId, !wasBookmarked);
    }

    api
      .post('/api/bookmarks/' + postId)
      .then(function (response) {
        var bookmarked = response.data.bookmarked;
        // Reconcile if server disagrees with optimistic update
        if (bookmarked === wasBookmarked) {
          setBookmarkCount(function (prev) {
            return bookmarked ? prev + 1 : prev - 1;
          });
          if (props.onBookmarkToggle) {
            props.onBookmarkToggle(postId, bookmarked);
          }
        }
      })
      .catch(function (err) {
        // Revert optimistic update
        setBookmarkCount(function (prev) {
          return wasBookmarked ? prev + 1 : prev - 1;
        });
        if (props.onBookmarkToggle) {
          props.onBookmarkToggle(postId, wasBookmarked);
        }
        console.error('Bookmark toggle failed:', err);
      });
  }

  var openUserList = ul.open;

  // Derive play state from context
  var isThisPostPlaying = playback.currentTrack && playback.currentTrack.postId === props.post.id;
  var playState: 'idle' | 'loading' | 'playing' | 'paused' = 'idle';
  if (isThisPostPlaying) {
    playState = playback.playbackState as any;
  }

  function buildQueueItems(trackData: any): any[] {
    // trackData is either { previewURL, catalogID } for a song, or { tracks: [...] } for an album
    var artworkURL = media.artwork_url;
    var artist = media.artist;

    if (trackData.tracks) {
      // Album — one item per track
      return trackData.tracks.map(function (t: any) {
        return {
          postId: props.post.id,
          title: t.title,
          artist: artist,
          artworkURL: artworkURL,
          catalogID: t.catalogID || null,
          previewURL: t.previewURL || null,
        };
      });
    }

    // Single song
    var songTitle = media.type === 'album' ? media.album_title : media.song_title;
    return [
      {
        postId: props.post.id,
        title: songTitle,
        artist: artist,
        artworkURL: artworkURL,
        catalogID: trackData.catalogID || null,
        previewURL: trackData.previewURL || null,
      },
    ];
  }

  function fetchAndPlay(mode: 'playNow' | 'addToQueue') {
    var songTitle = media.type === 'album' ? media.album_title : media.song_title;
    trackEvent(mode === 'playNow' ? 'music_played' : 'music_queued', {
      post_id: props.post.id,
      media_type: media.type,
      artist: media.artist,
      title: songTitle,
    });
    setFetchingPreview(true);
    setMenuPos(null);

    if (media.type === 'album' && media.upc) {
      // Fetch all album tracks
      api
        .get('/api/music/album-tracks', { params: { upc: media.upc } })
        .then(function (response) {
          setFetchingPreview(false);
          var items = buildQueueItems({ tracks: response.data.tracks || [] });
          if (items.length === 0) return;
          if (mode === 'playNow') {
            playback.playNow(items);
          } else {
            playback.addToQueue(items);
          }
        })
        .catch(function () {
          setFetchingPreview(false);
        });
    } else {
      // Song — use cached preview data or fetch it
      if (cachedPreviewRef.current) {
        setFetchingPreview(false);
        var items = buildQueueItems(cachedPreviewRef.current);
        if (mode === 'playNow') {
          playback.playNow(items);
        } else {
          playback.addToQueue(items);
        }
        return;
      }

      var params = media.isrc ? { isrc: media.isrc } : { upc: media.upc };
      api
        .get('/api/music/preview', { params: params })
        .then(function (response) {
          var previewURL = response.data.previewURL || null;
          var catalogID = response.data.catalogID || null;
          cachedPreviewRef.current = { previewURL: previewURL, catalogID: catalogID };
          setFetchingPreview(false);
          var items = buildQueueItems({ previewURL: previewURL, catalogID: catalogID });
          if (mode === 'playNow') {
            playback.playNow(items);
          } else {
            playback.addToQueue(items);
          }
        })
        .catch(function () {
          setFetchingPreview(false);
        });
    }
  }

  function playNow() {
    if (props.onPlayNow) {
      setMenuPos(null);
      trackEvent('music_played', {
        post_id: props.post.id,
        media_type: media.type,
        artist: media.artist,
        title: media.type === 'album' ? media.album_title : media.song_title,
        from_list: true,
      });
      props.onPlayNow();
      return;
    }
    fetchAndPlay('playNow');
  }

  function handlePlay(e: any) {
    // If this post is currently playing or paused, toggle
    if (isThisPostPlaying) {
      playback.togglePlayPause();
      return;
    }

    // Show preview-vs-full explainer once for users not connected to Apple Music
    if (!playback.isAppleMusicConnected) {
      try {
        if (!localStorage.getItem('hr_seen_preview_explainer')) {
          var playRect = e.currentTarget.getBoundingClientRect();
          pendingPlayRect.current = { left: playRect.left, top: playRect.top };
          setShowPreviewExplainer(true);
          return;
        }
      } catch (_e) {
        /* localStorage unavailable — skip */
      }
    }

    // If nothing is playing, start immediately
    if (!isSomethingPlaying) {
      playNow();
      return;
    }

    // Something else is playing — show context menu at click position
    var rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ x: rect.left, y: rect.top });
  }

  function dismissPreviewExplainer() {
    try {
      localStorage.setItem('hr_seen_preview_explainer', '1');
    } catch (_e) {
      /* ok */
    }
    setShowPreviewExplainer(false);
  }

  function handlePreviewExplainerPlay() {
    dismissPreviewExplainer();
    // Continue with the original play action
    if (!isSomethingPlaying) {
      playNow();
    } else {
      var savedRect = pendingPlayRect.current;
      if (savedRect) {
        setMenuPos({ x: savedRect.left, y: savedRect.top });
      } else {
        playNow();
      }
    }
    pendingPlayRect.current = null;
  }

  function handlePreviewExplainerConnect() {
    dismissPreviewExplainer();
    pendingPlayRect.current = null;
    playback
      .authorize()
      .then(function () {
        playNow();
      })
      .catch(function () {
        // User cancelled auth — do nothing
      });
  }

  function getCatalogID(): Promise<string | null> {
    if (cachedPreviewRef.current && cachedPreviewRef.current.catalogID) {
      return Promise.resolve(cachedPreviewRef.current.catalogID);
    }
    var params: any = {};
    if (media.isrc) {
      params.isrc = media.isrc;
    } else if (media.upc) {
      params.upc = media.upc;
    } else {
      return Promise.resolve(null);
    }
    return api
      .get('/api/music/preview', { params: params })
      .then(function (response: any) {
        var catalogID = response.data.catalogID || null;
        var previewURL = response.data.previewURL || null;
        cachedPreviewRef.current = { previewURL: previewURL, catalogID: catalogID };
        return catalogID;
      })
      .catch(function () {
        return null;
      });
  }

  function getAlbumTrackCatalogIDs(): Promise<string[]> {
    if (!media.upc) return Promise.resolve([]);
    return api
      .get('/api/music/album-tracks', { params: { upc: media.upc } })
      .then(function (response: any) {
        var tracks = response.data.tracks || [];
        return tracks
          .map(function (t: any) {
            return t.catalogID;
          })
          .filter(function (id: string | null) {
            return !!id;
          });
      })
      .catch(function () {
        return [];
      });
  }

  function handleAddToLibraryClick(e: any) {
    if (!playback.isAppleMusicConnected) {
      setShowExplainer(true);
      return;
    }
    handleAddToMusic();
  }

  function handleExplainerConnect() {
    setShowExplainer(false);
    playback
      .authorize()
      .then(function () {
        handleAddToMusic();
      })
      .catch(function () {
        // User cancelled auth — do nothing
      });
  }

  function handleAddToMusic() {
    setAddMenuPos(null);
    setAddState('adding');

    var instance = (window as any).MusicKit && (window as any).MusicKit.getInstance();
    if (!instance) {
      setAddState('error');
      return;
    }

    // Get track catalog IDs
    var trackIDsPromise: Promise<string[]>;
    if (media.type === 'album') {
      trackIDsPromise = getAlbumTrackCatalogIDs();
    } else {
      trackIDsPromise = getCatalogID().then(function (id) {
        return id ? [id] : [];
      });
    }

    // Ensure fresh auth token before making library calls
    instance
      .authorize()
      .then(function () {
        return trackIDsPromise;
      })
      .then(function (trackIDs: string[]) {
        if (trackIDs.length === 0) throw new Error('No tracks found');

        // Direct library add via query params (Apple's documented API format)
        var idsParam = encodeURIComponent(trackIDs.join(','));
        return instance.api
          .music('/v1/me/library?ids[songs]=' + idsParam, null, {
            fetchOptions: { method: 'POST' },
          })
          .catch(function () {
            // 202 with empty body can cause MusicKit to throw on response parsing — that's OK
          });
      })
      .then(function () {
        setAddState('addedLibrary');
        setLibraryAddCount(function (prev: number) {
          return prev + 1;
        });

        // Fire-and-forget: backend also adds to playlist + records analytics
        var freshToken = instance.musicUserToken || '';
        api
          .post('/api/music/add-to-library', {
            isrc: media.isrc || null,
            upc: media.upc || null,
            type: media.type,
            postID: props.post.id,
            musicUserToken: freshToken || undefined,
          })
          .catch(function () {
            /* ok if fails */
          });
      })
      .catch(function (err: any) {
        console.error('Add to library failed:', err);
        setAddState('error');
      });
  }

  var addButtonLabel = '+ Add';
  if (addState === 'adding') addButtonLabel = 'Adding...';
  if (addState === 'error') addButtonLabel = 'Not available';
  var isAdded = addState === 'addedLibrary';

  var isLoadingPlay = playState === 'loading' || fetchingPreview;
  var playIcon: React.ReactNode = <PlayIcon width="16" height="16" />;
  if (isLoadingPlay) playIcon = '...';
  if (playState === 'playing') playIcon = <PauseIcon width="16" height="16" />;

  var userInitial = user.username ? user.username.charAt(0).toUpperCase() : '?';
  var isCompact = props.variant === 'compact';
  var isSong = media.song_title != null && media.song_title !== '';
  var compactYear = media.release_date ? media.release_date.slice(0, 4) : null;
  var compactAlbumLine =
    isSong && media.album_title
      ? media.album_title + (compactYear ? ' \u00B7 ' + compactYear : '')
      : compactYear || '';

  function handleCompactTap(e: React.MouseEvent) {
    // Don't navigate if user clicked a button, link, or interactive element
    var target = e.target as HTMLElement;
    while (target && target !== e.currentTarget) {
      var tag = target.tagName;
      if (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' || tag === 'TEXTAREA') return;
      target = target.parentElement as HTMLElement;
    }
    navigate('/post/' + props.post.id);
  }

  return (
    <>
      <PostDiv
        onClick={isCompact ? handleCompactTap : undefined}
        style={isCompact ? { cursor: 'pointer' } : undefined}
      >
        {/* Shared-by banner */}
        {props.post.sharedBy && props.post.sharedBy.length > 0 && (
          <SharedByBanner>
            <span style={{ fontSize: '1.1em' }}>&#8634;</span>
            {props.post.sharedBy.length === 1 && (
              <span>
                <SharedByLink to={'/profile/' + props.post.sharedBy[0].username}>
                  {props.post.sharedBy[0].username}
                </SharedByLink>
                {' reposted'}
              </span>
            )}
            {props.post.sharedBy.length === 2 && (
              <span>
                <SharedByLink to={'/profile/' + props.post.sharedBy[0].username}>
                  {props.post.sharedBy[0].username}
                </SharedByLink>
                {' and '}
                <SharedByLink to={'/profile/' + props.post.sharedBy[1].username}>
                  {props.post.sharedBy[1].username}
                </SharedByLink>
                {' reposted'}
              </span>
            )}
            {props.post.sharedBy.length > 2 && (
              <span>
                <SharedByLink to={'/profile/' + props.post.sharedBy[0].username}>
                  {props.post.sharedBy[0].username}
                </SharedByLink>
                {', '}
                <SharedByLink to={'/profile/' + props.post.sharedBy[1].username}>
                  {props.post.sharedBy[1].username}
                </SharedByLink>
                {', and ' +
                  (props.post.sharedBy.length - 2) +
                  ' other' +
                  (props.post.sharedBy.length - 2 > 1 ? 's' : '') +
                  ' reposted'}
              </span>
            )}
          </SharedByBanner>
        )}
        {/* 1. Header: avatar + username + time + "..." or Follow. A list
            item shows its rank here instead: the list's header card already
            names the author, once. */}
        <UserDiv>
          {props.listItem ? (
            props.listItem.gridSpot != null ? (
              <GridSpot index={props.listItem.gridSpot} size={28} />
            ) : (
              props.listItem.number != null && (
                <RankChip role="img" aria-label={'Number ' + props.listItem.number}>
                  {props.listItem.ranked
                    ? '#' + props.listItem.number
                    : props.listItem.number + '.'}
                </RankChip>
              )
            )
          ) : (
            <>
              {isSpecialAvatarUser(user.username) ? (
                <SpecialAvatar size={28} />
              ) : user.profilePictureURL ? (
                <ProfilePic src={API_BASE + user.profilePictureURL} alt={user.username} />
              ) : (
                <AvatarCircle bgColor={avatarColor(user.username)}>{userInitial}</AvatarCircle>
              )}
              <UserLink to={'/profile/' + user.username}>{user.username}</UserLink>

              <TimeAgoText style={isCompact ? { color: '#bbb' } : undefined}>
                {timeAgo(props.post.created_at)}
              </TimeAgoText>
            </>
          )}
          <UserSpacer />
          {!props.listItem &&
            isLoggedIn &&
            !isOwnPost &&
            props.onFollowToggle &&
            !props.isFollowing && (
              <MiniFollowButton isFollowing={false} onClick={handleFollowToggle}>
                Follow
              </MiniFollowButton>
            )}
          {renderListenButton()}
          {isLoggedIn && (
            <IconButton onClick={handlePostMenuClick}>
              <ThreeDotsIcon width="18" height="18" />
            </IconButton>
          )}
        </UserDiv>

        {/* 2. Music section: full artwork (expanded) or compact thumbnail row */}
        {isCompact ? (
          <CompactMusicRow>
            <CompactThumbWrapper>
              <CompactThumb
                src={(media.artwork_url || '').replace('{w}x{h}', '120x120')}
                alt={media.artist + ' - ' + title}
              />
              {canPlay && (
                <ThumbPlayButton
                  state={isLoadingPlay ? 'loading' : playState}
                  onClick={handlePlay}
                  disabled={isLoadingPlay}
                />
              )}
            </CompactThumbWrapper>
            <CompactMusicText>
              <CompactArtistLine>{media.artist}</CompactArtistLine>
              <CompactTrackLine>{title}</CompactTrackLine>
              {compactAlbumLine && <CompactAlbumLine>{compactAlbumLine}</CompactAlbumLine>}
            </CompactMusicText>
          </CompactMusicRow>
        ) : (
          <ArtworkBlock>
            <MediaHeader
              media={media}
              playButton={
                canPlay ? (
                  <IconButton
                    onClick={handlePlay}
                    disabled={isLoadingPlay}
                    style={{ color: 'white', fontSize: '1.2rem' }}
                  >
                    {playIcon}
                  </IconButton>
                ) : undefined
              }
            />
            <MediaArt media={media} size="300x300" />
          </ArtworkBlock>
        )}

        {/* 3. Post text - single-line preview in compact (tap to expand inline), full in expanded */}
        {isCompact &&
          displayText &&
          (function () {
            var firstLine = displayText.split('\n')[0].trim();
            var hasMore =
              displayText.indexOf('\n') !== -1 || displayText.trim().length > firstLine.length;
            return textExpanded ? (
              <PostTextDiv>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatText(
                      displayText.trimEnd(),
                      props.post.musicMentions,
                      props.post.media?.artist
                    ),
                  }}
                />
              </PostTextDiv>
            ) : (
              <CompactNoteRow
                onClick={
                  hasMore
                    ? function (e: any) {
                        e.stopPropagation();
                        setTextExpanded(true);
                      }
                    : undefined
                }
              >
                <CompactNoteText>{firstLine}</CompactNoteText>
                {hasMore && <CompactMoreLink>more...</CompactMoreLink>}
              </CompactNoteRow>
            );
          })()}
        {!isCompact &&
          (editor.editing ? (
            <PostEditForm editor={editor} />
          ) : (
            displayText && (
              <PostTextDiv>
                <PostTextInner
                  ref={postTextRef}
                  collapsed={!props.fullText && !textExpanded && (textOverflows || !textMeasured)}
                  onClick={
                    textOverflows && !textExpanded && !props.fullText
                      ? function () {
                          setTextExpanded(true);
                        }
                      : undefined
                  }
                  style={
                    textOverflows && !textExpanded && !props.fullText
                      ? { cursor: 'pointer' }
                      : undefined
                  }
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatText(
                        displayText.trimEnd(),
                        props.post.musicMentions,
                        props.post.media?.artist
                      ),
                    }}
                  />
                  {!props.fullText && !textExpanded && textOverflows && <PostTextFade />}
                </PostTextInner>
                {textOverflows && !props.fullText && !textExpanded && (
                  <ReadMoreButton
                    onClick={function () {
                      setTextExpanded(true);
                    }}
                  >
                    Read more
                  </ReadMoreButton>
                )}
              </PostTextDiv>
            )
          ))}

        {/* 5. Combined bottom row: split reaction pills + add button + bookmark pill */}
        <div style={{ position: 'relative' }}>
          {isCompact ? (
            <CompactBottomRow>
              <BottomLeft>
                {props.post.prompt && (
                  <PromptPillLink to={'/prompts/' + props.post.prompt.id}>
                    {props.post.prompt.name || props.post.prompt.text}
                  </PromptPillLink>
                )}
                <ReactionBar
                  reactions={rx.reactions}
                  userReactions={props.userReactions}
                  isLoggedIn={isLoggedIn}
                  onToggle={rx.toggle}
                  onShowReactors={function (emoji) {
                    openUserList({ type: 'reaction', emoji: emoji });
                  }}
                />
              </BottomLeft>
              <BottomRight>
                <CommentPill
                  onClick={function () {
                    openCommentSheet();
                  }}
                  hasCount={comments.length > 0}
                >
                  <CommentIcon />
                  {comments.length > 0 && (
                    <span>
                      {comments.reduce(function (total, c) {
                        return total + 1 + (c.replies ? c.replies.length : 0);
                      }, 0)}
                    </span>
                  )}
                </CommentPill>
                {(bookmarkCount > 0 || isLoggedIn) && (
                  <SplitPill borderColor="#ccc" bgColor="white">
                    <PillLeft
                      bgColor={props.isBookmarked ? '#e5e5e5' : 'transparent'}
                      hoverBg="#e5e5e5"
                      onClick={isLoggedIn ? handleBookmarkToggle : undefined}
                      style={!isLoggedIn ? { cursor: 'default' } : undefined}
                    >
                      {props.isBookmarked ? (
                        <BookmarkFillIcon width="16" height="16" />
                      ) : (
                        <BookmarkIcon width="16" height="16" />
                      )}
                    </PillLeft>
                    {bookmarkCount > 0 && (
                      <>
                        <PillDivider color="#ccc" />
                        <PillRight
                          bgColor="transparent"
                          hoverBg="#e5e5e5"
                          onClick={
                            isLoggedIn
                              ? function () {
                                  openUserList({ type: 'bookmark' });
                                }
                              : undefined
                          }
                          style={!isLoggedIn ? { cursor: 'default' } : undefined}
                        >
                          {bookmarkCount}
                        </PillRight>
                      </>
                    )}
                  </SplitPill>
                )}
              </BottomRight>
            </CompactBottomRow>
          ) : (
            <BottomRow>
              <BottomLeft>
                {props.post.prompt && (
                  <PromptPillLink to={'/prompts/' + props.post.prompt.id}>
                    {props.post.prompt.name || props.post.prompt.text}
                  </PromptPillLink>
                )}
                <ReactionBar
                  reactions={rx.reactions}
                  userReactions={props.userReactions}
                  isLoggedIn={isLoggedIn}
                  onToggle={rx.toggle}
                  onShowReactors={function (emoji) {
                    openUserList({ type: 'reaction', emoji: emoji });
                  }}
                />
              </BottomLeft>
              {/* Comment + Bookmark pills */}
              <BottomRight>
                {!props.fullText && (
                  <CommentPill
                    onClick={function () {
                      openCommentSheet();
                    }}
                    hasCount={comments.length > 0}
                  >
                    <CommentIcon />
                    {comments.length > 0 && (
                      <span>
                        {comments.reduce(function (total, c) {
                          return total + 1 + (c.replies ? c.replies.length : 0);
                        }, 0)}
                      </span>
                    )}
                  </CommentPill>
                )}
                {(bookmarkCount > 0 || isLoggedIn) && (
                  <>
                    <SplitPill borderColor="#ccc" bgColor="white">
                      <PillLeft
                        bgColor={props.isBookmarked ? '#e5e5e5' : 'transparent'}
                        hoverBg="#e5e5e5"
                        onClick={isLoggedIn ? handleBookmarkToggle : undefined}
                        style={!isLoggedIn ? { cursor: 'default' } : undefined}
                      >
                        {props.isBookmarked ? (
                          <BookmarkFillIcon width="16" height="16" />
                        ) : (
                          <BookmarkIcon width="16" height="16" />
                        )}
                      </PillLeft>
                      {bookmarkCount > 0 && (
                        <>
                          <PillDivider color="#ccc" />
                          <PillRight
                            bgColor="transparent"
                            hoverBg="#e5e5e5"
                            onClick={
                              isLoggedIn
                                ? function () {
                                    openUserList({ type: 'bookmark' });
                                  }
                                : undefined
                            }
                            style={!isLoggedIn ? { cursor: 'default' } : undefined}
                          >
                            {bookmarkCount}
                          </PillRight>
                        </>
                      )}
                    </SplitPill>
                  </>
                )}
              </BottomRight>
            </BottomRow>
          )}
        </div>

        {/* 6. Inline comment preview (feed view only, hidden when desktop inline comments open) */}
        {!props.fullText &&
          !showInlineComments &&
          comments.length > 0 &&
          (function () {
            var inlineComments = comments
              .filter(function (c) {
                return !c.parentCommentId;
              })
              .slice(0, 2);
            var totalCount = comments.reduce(function (total, c) {
              return total + 1 + (c.replies ? c.replies.length : 0);
            }, 0);
            return (
              <InlineCommentsSection>
                {inlineComments.map(function (comment) {
                  return (
                    <InlineCommentRow
                      key={comment.id}
                      onClick={function () {
                        openCommentSheet();
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {isSpecialAvatarUser(comment.user.username) ? (
                        <SpecialAvatar size={22} />
                      ) : comment.user.profilePictureURL ? (
                        <InlineCommentAvatar
                          src={API_BASE + comment.user.profilePictureURL}
                          alt={comment.user.username}
                        />
                      ) : (
                        <InlineCommentAvatarCircle $bgColor={avatarColor(comment.user.username)}>
                          {comment.user.username.charAt(0).toUpperCase()}
                        </InlineCommentAvatarCircle>
                      )}
                      <InlineCommentBody>
                        <InlineCommentClamp>
                          <InlineCommentUsername
                            to={'/profile/' + comment.user.username}
                            onClick={function (e: any) {
                              e.stopPropagation();
                            }}
                          >
                            {comment.user.username}
                          </InlineCommentUsername>
                          <InlineCommentText
                            dangerouslySetInnerHTML={{
                              __html: formatText(
                                comment.text,
                                comment.musicMentions,
                                props.post.media?.artist
                              ),
                            }}
                          />
                        </InlineCommentClamp>
                        {isLoggedIn && (
                          <InlineReplyLink
                            onClick={function (e: any) {
                              e.stopPropagation();
                              setReplyTarget({
                                commentId: comment.id,
                                username: comment.user.username,
                              });
                              openCommentSheet(true);
                            }}
                          >
                            Reply
                          </InlineReplyLink>
                        )}
                      </InlineCommentBody>
                    </InlineCommentRow>
                  );
                })}
                {totalCount > inlineComments.length && (
                  <ViewAllCommentsLink
                    onClick={function () {
                      openCommentSheet();
                    }}
                  >
                    View all {totalCount} comments
                  </ViewAllCommentsLink>
                )}
                {isLoggedIn && !showInlineComments && (
                  <InlineReplyPlaceholder
                    onClick={function () {
                      openCommentSheet(true);
                    }}
                  >
                    Add a comment...
                  </InlineReplyPlaceholder>
                )}
              </InlineCommentsSection>
            );
          })()}
        {!props.fullText && comments.length === 0 && isLoggedIn && !showInlineComments && (
          <InlineCommentsSection>
            <InlineReplyPlaceholder
              onClick={function () {
                openCommentSheet(true);
              }}
              $noBorder
            >
              Add a comment...
            </InlineReplyPlaceholder>
          </InlineCommentsSection>
        )}

        {/* 7. Comments (desktop inline) */}
        {showInlineComments && !props.fullText && (
          <CommentsSection>
            <Comments
              post={props.post}
              comments={comments}
              onCommentAdded={handleCommentAdded}
              fullText={true}
              likedCommentsMap={props.likedCommentsMap}
              onCommentLikeToggle={props.onCommentLikeToggle}
              onCommentDeleted={handleCommentDeleted}
              onCommentEdited={handleCommentEdited}
              autoFocus={wantsFocusRef.current}
            />
          </CommentsSection>
        )}

        {/* 7b. Comments (full view / post detail page) */}
        {props.fullText && (
          <CommentsSection>
            <Comments
              post={props.post}
              comments={comments}
              onCommentAdded={handleCommentAdded}
              fullText={props.fullText}
              likedCommentsMap={props.likedCommentsMap}
              onCommentLikeToggle={props.onCommentLikeToggle}
              onCommentDeleted={handleCommentDeleted}
              onCommentEdited={handleCommentEdited}
            />
          </CommentsSection>
        )}
        {/* Admin play counts */}
        {playCounts &&
          (playCounts.fullPlays > 0 ||
            playCounts.previewPlays > 0 ||
            playCounts.serviceOpens > 0) && (
            <AdminPlayCounts>
              {playCounts.fullPlays > 0 && <span>{playCounts.fullPlays} full</span>}
              {playCounts.previewPlays > 0 && <span>{playCounts.previewPlays} preview</span>}
              {playCounts.serviceOpens > 0 && <span>{playCounts.serviceOpens} opens</span>}
            </AdminPlayCounts>
          )}
      </PostDiv>
      {menuPos && (
        <>
          <QueueMenuOverlay
            onClick={function () {
              setMenuPos(null);
            }}
          />
          <QueueMenuPopup style={clampMenuPos(menuPos.x, menuPos.y - 80, 180, 80)}>
            <QueueMenuItem
              onClick={function () {
                playNow();
              }}
            >
              Play Now
            </QueueMenuItem>
            <QueueMenuItem
              onClick={function () {
                fetchAndPlay('addToQueue');
              }}
            >
              Add to Queue
            </QueueMenuItem>
          </QueueMenuPopup>
        </>
      )}
      {postMenuPos && (
        <>
          <QueueMenuOverlay
            onClick={function () {
              setPostMenuPos(null);
            }}
          />
          <QueueMenuPopup
            ref={function (el: HTMLDivElement | null) {
              if (!el || !postMenuPos) return;
              var menuW = el.offsetWidth || 200;
              var menuH = el.offsetHeight || 200;
              var vw = window.innerWidth;
              var vh = window.innerHeight;
              var left = Math.max(8, Math.min(postMenuPos.x - menuW, vw - menuW - 8));
              var top = postMenuPos.y + 4;
              if (top + menuH > vh - 8) {
                top = (postMenuPos.btnTop || postMenuPos.y) - menuH - 4;
              }
              top = Math.max(8, top);
              el.style.left = left + 'px';
              el.style.top = top + 'px';
            }}
            style={{
              maxHeight: '80vh',
              overflowY: 'auto' as const,
              position: 'fixed',
              zIndex: 1101,
            }}
          >
            <QueueMenuItem
              onClick={function () {
                setPostMenuPos(null);
                setShowShareModal(true);
              }}
            >
              Share...
            </QueueMenuItem>
            {/* A list item is not reposted on its own; the way to carry it
                out of the list is a post of your own about the same music. */}
            {isLoggedIn && isListItem && (
              <QueueMenuItem onClick={handlePostAboutItem}>
                {media.type === 'album' ? 'Post about this album' : 'Post about this song'}
              </QueueMenuItem>
            )}
            {isLoggedIn && !isOwnPost && !isListItem && props.onRepostToggle && (
              <QueueMenuItem
                onClick={handleRepostToggle}
                style={props.isReposted ? { color: '#2ecc71' } : undefined}
              >
                {props.isReposted ? 'Undo Repost' : 'Repost'}
              </QueueMenuItem>
            )}
            {renderServiceItems(function () {
              setPostMenuPos(null);
            })}
            {isOwnPost && !isListItem && (
              <QueueMenuItem onClick={handleEditStart}>Edit Post</QueueMenuItem>
            )}
            {isOwnPost && !isListItem && props.onPostDelete && (
              <QueueMenuItem onClick={handlePostDelete} style={{ color: 'tomato' }}>
                Delete Post
              </QueueMenuItem>
            )}
          </QueueMenuPopup>
        </>
      )}
      {listenPos && (
        <>
          <QueueMenuOverlay
            onClick={function () {
              setListenPos(null);
            }}
          />
          <QueueMenuPopup
            ref={function (el: HTMLDivElement | null) {
              if (!el || !listenPos) return;
              var menuW = el.offsetWidth || 200;
              var menuH = el.offsetHeight || 200;
              var vw = window.innerWidth;
              var vh = window.innerHeight;
              var left = Math.max(8, Math.min(listenPos.x - menuW, vw - menuW - 8));
              var top = listenPos.y + 4;
              if (top + menuH > vh - 8) {
                top = (listenPos.btnTop || listenPos.y) - menuH - 4;
              }
              top = Math.max(8, top);
              el.style.left = left + 'px';
              el.style.top = top + 'px';
            }}
            style={{
              maxHeight: '80vh',
              overflowY: 'auto' as const,
              position: 'fixed',
              zIndex: 1101,
            }}
          >
            {renderServiceItems(function () {
              setListenPos(null);
            })}
          </QueueMenuPopup>
        </>
      )}
      {showExplainer && (
        <ExplainerOverlay
          onClick={function () {
            setShowExplainer(false);
          }}
        >
          <ExplainerCard
            onClick={function (e: any) {
              e.stopPropagation();
            }}
          >
            <ExplainerTitle>Add to Apple Music</ExplainerTitle>
            <ExplainerBody>
              To save this music to your library or a playlist, connect your Apple Music account.
              You'll be asked to sign in with your Apple&nbsp;ID.
            </ExplainerBody>
            <ExplainerConnectButton onClick={handleExplainerConnect}>
              Connect to Apple Music
            </ExplainerConnectButton>
          </ExplainerCard>
        </ExplainerOverlay>
      )}
      {showPreviewExplainer && (
        <ExplainerOverlay
          onClick={function () {
            dismissPreviewExplainer();
            pendingPlayRect.current = null;
          }}
        >
          <ExplainerCard
            onClick={function (e: any) {
              e.stopPropagation();
            }}
          >
            <button
              onClick={function () {
                dismissPreviewExplainer();
                pendingPlayRect.current = null;
              }}
              style={{
                position: 'absolute',
                top: '0.75rem',
                right: '0.75rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.1rem',
                color: '#999',
                lineHeight: 1,
                padding: 0,
              }}
              aria-label="Close"
            >
              &times;
            </button>
            <ExplainerTitle>
              How Listening Works on <HeavyText>Heavy</HeavyText>
              <LightItalicText>Rotation</LightItalicText>
            </ExplainerTitle>
            <ExplainerBody>
              We play short previews so you can get a taste before you dive in.
            </ExplainerBody>
            <ExplainerBody>
              If you have an Apple Music subscription, you can connect your account to stream full
              songs directly on <HeavyText>Heavy</HeavyText>
              <LightItalicText>Rotation</LightItalicText>.
            </ExplainerBody>
            <ExplainerBody>
              You can tap ••• at the top of a post to search for the music in your app of choice
              (see profile for options).
            </ExplainerBody>
            <ExplainerConnectButton onClick={handlePreviewExplainerConnect}>
              Connect to Apple Music
            </ExplainerConnectButton>
            <ExplainerSecondaryButton onClick={handlePreviewExplainerPlay}>
              Listen to Preview
            </ExplainerSecondaryButton>
            <ExplainerSecondaryButton
              onClick={function () {
                dismissPreviewExplainer();
                pendingPlayRect.current = null;
                navigate('/streaming');
              }}
            >
              Configure Playback Preferences
            </ExplainerSecondaryButton>
          </ExplainerCard>
        </ExplainerOverlay>
      )}
      <UserListModal modal={ul.modal} data={ul.data} loading={ul.loading} onClose={ul.close} />
      {showShareModal && (
        <ShareModal
          url={window.location.origin + '/post/' + props.post.id}
          text={
            (media.type === 'album' ? media.album_title : media.song_title) +
            ' by ' +
            media.artist +
            ' on HeavyRotation'
          }
          shareType="post"
          imageData={{
            artworkURL: media.artwork_url,
            songTitle: media.song_title,
            albumTitle: media.album_title,
            artist: media.artist,
            releaseDate: media.release_date,
            mediaType: media.type,
            username: props.post.user?.username,
            displayName: props.post.user?.username,
            profilePicURL: props.post.user?.profilePictureURL,
            note: props.post.text,
          }}
          onClose={function () {
            setShowShareModal(false);
          }}
        />
      )}
      <input
        ref={keyboardProxyRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          opacity: 0,
          top: '-100px',
          left: 0,
          width: '1px',
          height: '1px',
          border: 'none',
          padding: 0,
          fontSize: '16px',
        }}
        readOnly
      />
      {showCommentSheet && (
        <>
          <CommentSheetOverlay onClick={closeCommentSheet} />
          <CommentSheetContainer
            ref={sheetRef}
            $open={sheetAnimating}
            onClick={function (e: any) {
              e.stopPropagation();
            }}
          >
            <CommentSheetHeader
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
            >
              <CommentSheetHandle />
              <CommentSheetTitle>Comments</CommentSheetTitle>
            </CommentSheetHeader>
            <CommentSheetBody>
              <Comments
                post={props.post}
                comments={comments}
                onCommentAdded={handleCommentAdded}
                fullText={true}
                likedCommentsMap={props.likedCommentsMap}
                onCommentLikeToggle={props.onCommentLikeToggle}
                onCommentDeleted={handleCommentDeleted}
                onCommentEdited={handleCommentEdited}
                autoFocus={(comments.length === 0 || wantsFocusRef.current) && sheetReady}
                initialReplyToId={replyTarget?.commentId}
                initialReplyToUsername={replyTarget?.username}
                onClose={closeCommentSheet}
              />
            </CommentSheetBody>
          </CommentSheetContainer>
        </>
      )}
      {repostToast && <Toast $visible={!!repostToast}>{repostToast}</Toast>}
    </>
  );
}
